import React, { useState, useRef, useEffect, FC, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  useTheme,
  Avatar,
  Alert,
  Modal,
  List,
  ListItem,
  InputAdornment,
  Button // <-- CHANGE 1: Imported Button
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Evidence } from './EvidencePanel';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { keyframes } from '@emotion/react';


// --- START: CHAT HISTORY DIALOG COMPONENT ---

const YOUR_TEXTURE_SVG_PATH = '/textures/Texture.svg';
const YOUR_GRADIENT_SVG_PATH = '/images/Gradient_Popup.svg';

interface ChatSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

interface ChatHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectChat: (chatId: string) => void;
  previousChats: ChatSession[];
}

const groupChatsByDate = (chats: ChatSession[]): Record<string, ChatSession[]> => {
  const groups: Record<string, ChatSession[]> = { Today: [], Yesterday: [], "Last 7 days": [], Older: [] };
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const last7days = new Date(today);
  last7days.setDate(last7days.getDate() - 7);

  chats.forEach(chat => {
    const chatDate = new Date(chat.updatedAt);
    if (chatDate >= today) {
      groups.Today.push(chat);
    } else if (chatDate >= yesterday) {
      groups.Yesterday.push(chat);
    } else if (chatDate >= last7days) {
      groups["Last 7 days"].push(chat);
    } else {
      groups.Older.push(chat);
    }
  });

  return Object.fromEntries(Object.entries(groups).filter(([_, value]) => value.length > 0));
};

const ChatHistoryDialog: FC<ChatHistoryDialogProps> = ({ open, onClose, onSelectChat, previousChats }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredAndGroupedChats = useMemo(() => {
        if (!previousChats) return {};
        const filtered = previousChats.filter((chat: ChatSession) =>
            chat.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return groupChatsByDate(filtered);
    }, [previousChats, searchTerm]);

    const renderContent = () => {
        if (!previousChats) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
        if (Object.keys(filteredAndGroupedChats).length === 0 && searchTerm) return <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight:'500', textAlign: 'center', p: 4, color: '#333333' }}>No chats match your search.</Typography>;
        if (Object.keys(filteredAndGroupedChats).length === 0) return <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight:'500', textAlign: 'center', p: 4, color: '#333333' }}>No previous chats found.</Typography>;


        return (
            <Box>
                {Object.entries(filteredAndGroupedChats).map(([group, chats]) => (
                    <Box key={group} sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontFamily:'Inter', fontSize:'14px',fontWeight: 600, color: '#333333', my: 2, px: 3, lineHeight:'20px', letterSpacing: 'Letter Spacing', paragraphSpacing:10}}>
                            {group}
                        </Typography>
                        <List disablePadding>
                            {(chats as ChatSession[]).map((chat) => (
                                <ListItem
                                    key={chat.id}
                                    onClick={() => onSelectChat(chat.id)}
                                    sx={{
                                        py: 0.5, px: 3, cursor: 'pointer',
                                       
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: '#333333', border: '1px dashed #CCCCCC', borderRadius: '20px', p: '8px 14px', width: 'auto', '&:hover': { bgcolor: 'action.hover' } }}>
                                        {chat.title}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Modal open={open} onClose={onClose} closeAfterTransition>
            <Paper sx={{
                position: 'absolute', top: 16, right: 16,
                width: { xs: '90%', sm: 480 },
                height: 'calc(100% - 32px)',
                borderRadius: '16px',
                boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                bgcolor: '#FFFFFF',
                display: 'flex', flexDirection: 'column',
                outline: 'none',
            }}>
                <IconButton onClick={onClose} sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
                    <CloseIcon />
                </IconButton>
                <Box sx={{
                    display: 'flex', alignItems: 'center', mb: 2, flexShrink: 0,
                    backgroundImage: `url(${YOUR_TEXTURE_SVG_PATH}), url(${YOUR_GRADIENT_SVG_PATH})`,
                    backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center',
                    borderRadius: '8px', p: 1, minHeight: '120px',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
                        <Box sx={{
                            width: 56, height: 56, borderRadius: '10px', backgroundColor: '#E5EFFF',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <img src="/icons/history-fill-blue.svg" alt="Previous Chats" style={{ width: '28px', height: '28px' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '18px', color: '#333333' }}>
                            Previous Chats
                        </Typography>
                    </Box>
                    
                </Box>
                 <Box sx={{ px: 3, pb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Search chats"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>,
                            sx: {
                      fontFamily:'Inter',
                      fontWeight: 400,
                      fontSize: '14px',
                      borderRadius: '8px',
                      bgcolor: '#F9F9F9',
                      
                      height:'auto',
                      '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0064FF'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0064FF'
                      }
                    }
                        }}
                    />
                </Box>
                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    {renderContent()}
                </Box>
            </Paper>
        </Modal>
    );
};


// --- END: CHAT HISTORY DIALOG COMPONENT ---


const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// GraphQL Definitions
const GET_CHAT_HISTORY_QUERY = gql`
  query GetChatHistory($sessionId: ID!) {
    getChatHistory(sessionId: $sessionId) {
      id
      role
      content
      metadata
      createdAt
    }
  }
`;

const POST_MESSAGE_MUTATION = gql`
  mutation PostMessage($input: PostMessageInput!) {
    postMessage(input: $input) {
      id
      role
      content
      metadata
      createdAt
    }
  }
`;

// Interfaces
interface ChatMessage {
  id: string;
  sender: 'user' | 'agent';
  text: string;
  timestamp: string;
  avatar?: string;
  name?: string;
  hasEvidence: boolean;
  rawMessage: ChatHistoryMessage;
}

interface ChatHistoryMessage {
    id: string;
    role: 'user' | 'agent';
    content: string;
    metadata: {
      evidence: { type: string, k8s_object: string, data: string }[];
      [key: string]: any;
    } | null;
    createdAt: string;
}

interface ChatDetailViewProps {
  chatId: string;
  userName?: string | null;
  userProfilePicture?: string | undefined;
  onBack: () => void;
  onMessageSelect: (evidences: Evidence[]) => void;
  initialPrompt: string | null;
  onInitialPromptPosted: () => void;
  previousChats: ChatSession[];
  onNavigateToChat: (chatId: string) => void;
}

interface CodeComponentProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const formatDateDetail = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
    return new Date(dateString).toLocaleString('en-US', options);
};

const mapApiTypeToUiType = (apiType: string = 'unknown'): Evidence['type'] => {
    const type = apiType.toLowerCase();
    switch (type) {
        case 'log':
        case 'metrics':
        case 'text':
            return type;
        case 'yaml':
            return 'code';
        case 'json':
          return 'log';
        default:
            return 'unknown';
    }
}

const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
    const evidenceItems = msg.metadata?.evidence;
    if (!evidenceItems || !Array.isArray(evidenceItems) || evidenceItems.length === 0) {
        return [];
    }
    return evidenceItems.map((item, index) => {
        let details = item.data;
        let resource = item.k8s_object || 'Details';
        try {
            const parsed = JSON.parse(item.data);
            details = JSON.stringify(parsed, null, 2);
            if (resource === 'Details' && parsed.name) {
                resource = parsed.name;
            }
        } catch (e) { /* Not a JSON string, use raw data */ }
        
        return {
            id: `${msg.id}-evidence-${index}`,
            type: mapApiTypeToUiType(item.type),
            project: 'N/A',
            resource: resource,
            timestamp: msg.createdAt,
            status: 'Normal',
            details,
        };
    });
};


const ChatDetailView: FC<ChatDetailViewProps> = ({ chatId, userName, userProfilePicture,onBack, onMessageSelect, initialPrompt, onInitialPromptPosted, previousChats, onNavigateToChat }) => {
  const theme = useTheme();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const finOpsIconUrl = '/images/Chatbot.svg';

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  // --- CHANGE 2: Added state for send message error ---
  const [sendError, setSendError] = useState<string | null>(null);
  
  const handleSelectChatFromDialog = (newChatId: string) => {
      setIsHistoryOpen(false);
      onNavigateToChat(newChatId); 
  };
  
  const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
    id: msg.id,
    sender: msg.role,
    text: msg.content,
    timestamp: msg.createdAt,
    name: msg.role === 'user' ? (userName || 'User') : 'Copilot',
    hasEvidence: !!msg.metadata?.evidence?.length,
    rawMessage: msg,
  });

  // --- CHANGE 3: Extracted refetch from useQuery ---
  const { loading: isLoadingHistory, error: historyError, data, refetch: refetchHistory } = useQuery(GET_CHAT_HISTORY_QUERY, {
    variables: { sessionId: chatId },
    skip: !chatId || !!initialPrompt,
    fetchPolicy: 'network-only'
  });

   useEffect(() => {
    if (data?.getChatHistory) {
      let processedHistory: ChatHistoryMessage[] = [...data.getChatHistory];
      if (processedHistory.length > 2 && processedHistory[0].role === 'user' && processedHistory[2].role === 'agent') {
         processedHistory = processedHistory.filter((_, index) => index !== 0 && index !== 2);
      }
      const historyMessages = processedHistory.map(mapApiMessageToChatMessage);
      setMessages(historyMessages);
    }
  }, [data]);

  // --- CHANGE 4: Updated mutation's onError handler ---
  const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
    onCompleted: (data) => {
        const agentMessage = mapApiMessageToChatMessage(data.postMessage);
        setMessages(prev => [...prev, agentMessage]);
        const newEvidences = parseAndMapEvidence(data.postMessage);
        if (newEvidences.length > 0) {
            onMessageSelect(newEvidences);
        }
        if (initialPrompt) {
            onInitialPromptPosted();
        }
    },
    onError: (error) => {
        console.error("Failed to post message:", error);
        // Remove the temporary message that was added for optimistic UI
        setMessages(prev => prev.filter(m => !m.id.startsWith('temp-user-')));
        // Set the error state to show an alert to the user
        setSendError(error.message || "Failed to send message. Please try again.");
    }
  });

  useEffect(() => {
    if (initialPrompt && chatId) {
      const newUserMessage: ChatMessage = {
        id: `temp-user-${Date.now()}`,
        sender: 'user', text: initialPrompt, timestamp: new Date().toISOString(),
        name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
      };
      setMessages([newUserMessage]);
      postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
    }
  }, [initialPrompt, chatId, postMessage, userName]);

  const handleMessageClick = (message: ChatMessage) => {
    if (message.sender !== 'agent') {
      return;
    }
    if(selectedMessageId === message.id) {
      setSelectedMessageId(null);
      onMessageSelect([]);
      return;
    }
    setSelectedMessageId(message.id);
    const parsedEvidences = parseAndMapEvidence(message.rawMessage);
    onMessageSelect(parsedEvidences);
  }

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (input.trim() && !isSending) {
      setSendError(null); // Clear previous send error
      const userMessageText = input;
      setInput('');
      const newUserMessage: ChatMessage = {
        id: `temp-user-${Date.now()}`,
        sender: 'user', text: userMessageText, timestamp: new Date().toISOString(),
        name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
      };
      setMessages(prev => [...prev, newUserMessage]);
      postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
    }
  };

  const renderMessageText = (text: string, sender: 'user' | 'agent') => {
    const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
    const baseFontStyles = { fontFamily: 'Inter', fontSize: '14px', fontWeight:400 };

    return (
      <Box sx={baseFontStyles}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ node, children }) => (<Typography component="p" sx={{ fontSize: 'inherit', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{children}</Typography>),
            h1: ({ node, children }) => <Typography variant="h6" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
            h2: ({ node, children }) => <Typography variant="h6" component="h2" sx={{ fontSize: '1.15rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
            h3: ({ node, children }) => <Typography variant="h6" component="h3" sx={{ fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
            ul: ({ node, children }) => <Box component="ul" sx={{ pl: 3, my: 1 }}>{children}</Box>,
            ol: ({ node, children }) => <Box component="ol" sx={{ pl: 3, my: 1 }}>{children}</Box>,
            li: ({ node, children }) => (<Typography component="li" sx={{ fontSize: 'inherit', mb: 0.5 }}>{children}</Typography>),
            a: ({ node, ...props }) => (<a {...props} style={{ color: linkColor, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" />),
            code({ node, inline, className, children, ...props }: CodeComponentProps) {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <SyntaxHighlighter style={vscDarkPlus as any} language={match[1]} PreTag="div" >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code {...props} style={{ fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.05)', color: '#333', padding: '2px 5px', borderRadius: '4px' }}>
                  {children}
                </code>
              );
            },
          }}
        >
          {text}
        </ReactMarkdown>
      </Box>
    );
  };

  return (
      <Paper elevation={0} sx={{ width:'100%', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0, zIndex: 1 }}>
            <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
            <Box sx={{display:'flex', alignItems:'center', gap:1.5, mr:0.8}}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
                <IconButton size="small" onClick={onBack}><img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} /></IconButton>
              </Box>
             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
                <IconButton size="small" onClick={() => setIsHistoryOpen(true)}>
                    <img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} />
                </IconButton>
             </Box>
            </Box>
        </Box>
        <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2, zIndex: 1, display: 'flex', flexDirection: 'column' }}>
          {isLoadingHistory && <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1}}><CircularProgress /></Box>}
          
          {/* --- CHANGE 5: Replaced history error Alert with full error display --- */}
          {historyError && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              p: 3,
              height: '100%',
            }}>
              <img src="/usmerror.svg" alt="Error" style={{ width: '130px', marginBottom: '16px' }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
                Unable to Load Chat
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '300px', mb: 2 }}>
                We're having trouble loading the chat history. Please try again.
              </Typography>
              <Button
                  variant="text"
                  onClick={() => refetchHistory()}
                  sx={{
                      textTransform: 'none',
                      color: '#0064FF',
                      fontWeight: 500,
                      fontFamily:'Inter',
                      lineHeight:'21px',
                      fontSize:'14px',
                  }}
                  >
                  Refresh Page
              </Button>
            </Box>
          )}

          {!isLoadingHistory && !historyError && messages.map((msg) => (
            <Box key={msg.id} onClick={() => handleMessageClick(msg)} sx={{ cursor:msg.sender === 'agent' ? 'pointer' : 'default', mb: 3 }}>
             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
                   <Avatar src={msg.sender === 'user' ? userProfilePicture : finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
                   <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
                   <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
                   <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
                </Box>
                <Box sx={{ 
                    bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', 
                    color: '#333333', p: 1.5, borderRadius: '20px', 
                    border: selectedMessageId === msg.id ? `1px solid ${theme.palette.primary.main}` : 'transparent', 
                    width: 'auto', alignSelf: msg.sender === 'agent' ? 'flex-start' : 'flex-end',
                    maxWidth: '100%', overflowX: 'auto'
                }}>
                    {renderMessageText(msg.text, msg.sender)}
                </Box>
             </Box>
            </Box>
          ))}
        </Box>
        {!isLoadingHistory && !historyError && (
          <Box>
        <img src="/icons/half-gradient.svg" alt="background gradient" style={{ position: 'absolute', bottom: '-10%', left: '-15%', width: '130%', height: 'auto', objectFit: 'contain', opacity: '60%', zIndex: 0, pointerEvents: 'none' }} />
       <Box sx={{ mt: 'auto', flexShrink: 0, zIndex: 1 }}>
            <Paper
                elevation={0}
                sx={{
                    p: '1.5px', borderRadius: '16px', bgcolor: 'transparent', minHeight: '64px',
                    display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
                    '::before': { 
                        content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        borderRadius: '16px', padding: '1.5px',
                        background: 'linear-gradient(92.06deg, rgba(6, 121, 255, 0.15) 7.21%, rgba(215, 77, 18, 0.16) 97.95%)',
                        backgroundSize: '200% 200%', animation: `${gradientAnimation} 3s linear infinite`,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor', maskComposite: 'exclude', zIndex: 0,
                    },
                }}
            >
                <Box sx={{
                    position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column',
                    flexGrow: 1, bgcolor: '#FFFFFF', borderRadius: '15px', p: '8px' 
                }}>
                    {/* --- CHANGE 6: Added alert for message send errors --- */}
                    {/* {sendError && (
                        <Alert severity="error" onClose={() => setSendError(null)} sx={{ mb: 1 }}>
                            {sendError}
                        </Alert>
                    )} */}
                    <TextField
                        fullWidth multiline variant="standard" placeholder="Enter your prompt here..."
                        value={input} onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend()) }
                        disabled={isSending || isLoadingHistory}
                        sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
                        InputProps={{ disableUnderline: true, style: { fontFamily: 'Inter', fontWeight: 400, fontSize: '14px' } }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                            color="primary" onClick={handleSend} disabled={isSending || isLoadingHistory || !input.trim()}
                            sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
                        >
                            {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
                        </IconButton>
                    </Box>
                </Box>
            </Paper>
        </Box>
        </Box>
        )}
        <ChatHistoryDialog
            open={isHistoryOpen}
            onClose={() => setIsHistoryOpen(false)}
            onSelectChat={handleSelectChatFromDialog}
            previousChats={previousChats}
        />
      </Paper>
  );
};

export default ChatDetailView;





// import React, { useState, useRef, useEffect, FC, useMemo } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
//   Modal,
//   List,
//   ListItem,
//   InputAdornment,
// } from '@mui/material';
// import { Close as CloseIcon } from '@mui/icons-material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel';
// import ReactMarkdown from 'react-markdown';
// import remarkGfm from 'remark-gfm';
// import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
// import { keyframes } from '@emotion/react';


// // --- START: CHAT HISTORY DIALOG COMPONENT ---

// const YOUR_TEXTURE_SVG_PATH = '/textures/Texture.svg';
// const YOUR_GRADIENT_SVG_PATH = '/images/Gradient_Popup.svg';

// interface ChatSession {
//   id: string;
//   title: string;
//   createdAt: string;
//   updatedAt: string;
// }

// interface ChatHistoryDialogProps {
//   open: boolean;
//   onClose: () => void;
//   onSelectChat: (chatId: string) => void;
//   previousChats: ChatSession[];
// }

// const groupChatsByDate = (chats: ChatSession[]): Record<string, ChatSession[]> => {
//   // --- MODIFICATION 1: Added "Older" to the groups object ---
//   const groups: Record<string, ChatSession[]> = { Today: [], Yesterday: [], "Last 7 days": [], Older: [] };
//   const now = new Date();
//   const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
//   const yesterday = new Date(today);
//   yesterday.setDate(yesterday.getDate() - 1);
//   const last7days = new Date(today);
//   last7days.setDate(last7days.getDate() - 7);

//   chats.forEach(chat => {
//     const chatDate = new Date(chat.updatedAt);
//     if (chatDate >= today) {
//       groups.Today.push(chat);
//     } else if (chatDate >= yesterday) {
//       groups.Yesterday.push(chat);
//     } else if (chatDate >= last7days) {
//       groups["Last 7 days"].push(chat);
//     } else {
//       // --- MODIFICATION 2: Added an else block to handle older chats ---
//       groups.Older.push(chat);
//     }
//   });

//   return Object.fromEntries(Object.entries(groups).filter(([_, value]) => value.length > 0));
// };

// const ChatHistoryDialog: FC<ChatHistoryDialogProps> = ({ open, onClose, onSelectChat, previousChats }) => {
//     const [searchTerm, setSearchTerm] = useState('');

//     const filteredAndGroupedChats = useMemo(() => {
//         if (!previousChats) return {};
//         const filtered = previousChats.filter((chat: ChatSession) =>
//             chat.title.toLowerCase().includes(searchTerm.toLowerCase())
//         );
//         return groupChatsByDate(filtered);
//     }, [previousChats, searchTerm]);

//     const renderContent = () => {
//         if (!previousChats) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
//         if (Object.keys(filteredAndGroupedChats).length === 0 && searchTerm) return <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight:'500', textAlign: 'center', p: 4, color: '#333333' }}>No chats match your search.</Typography>;
//         if (Object.keys(filteredAndGroupedChats).length === 0) return <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight:'500', textAlign: 'center', p: 4, color: '#333333' }}>No previous chats found.</Typography>;


//         return (
//             <Box>
//                 {Object.entries(filteredAndGroupedChats).map(([group, chats]) => (
//                     <Box key={group} sx={{ mb: 2 }}>
//                         <Typography variant="body2" sx={{ fontFamily:'Inter', fontSize:'14px',fontWeight: 600, color: '#333333', my: 2, px: 3, lineHeight:'20px', letterSpacing: 'Letter Spacing', paragraphSpacing:10}}>
//                             {group}
//                         </Typography>
//                         <List disablePadding>
//                             {(chats as ChatSession[]).map((chat) => (
//                                 <ListItem
//                                     key={chat.id}
//                                     onClick={() => onSelectChat(chat.id)}
//                                     sx={{
//                                         py: 0.5, px: 3, cursor: 'pointer',
                                       
//                                     }}
//                                 >
//                                     <Typography variant="body2" sx={{ color: '#333333', border: '1px dashed #CCCCCC', borderRadius: '20px', p: '8px 14px', width: 'auto', '&:hover': { bgcolor: 'action.hover' } }}>
//                                         {chat.title}
//                                     </Typography>
//                                 </ListItem>
//                             ))}
//                         </List>
//                     </Box>
//                 ))}
//             </Box>
//         );
//     };

//     return (
//         <Modal open={open} onClose={onClose} closeAfterTransition>
//             <Paper sx={{
//                 position: 'absolute', top: 16, right: 16,
//                 width: { xs: '90%', sm: 480 },
//                 height: 'calc(100% - 32px)',
//                 borderRadius: '16px',
//                 boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
//                 bgcolor: '#FFFFFF',
//                 display: 'flex', flexDirection: 'column',
//                 outline: 'none',
//             }}>
//                 <IconButton onClick={onClose} sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
//                     <CloseIcon />
//                 </IconButton>
//                 <Box sx={{
//                     display: 'flex', alignItems: 'center', mb: 2, flexShrink: 0,
//                     backgroundImage: `url(${YOUR_TEXTURE_SVG_PATH}), url(${YOUR_GRADIENT_SVG_PATH})`,
//                     backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center',
//                     borderRadius: '8px', p: 1, minHeight: '120px',
//                 }}>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
//                         <Box sx={{
//                             width: 56, height: 56, borderRadius: '10px', backgroundColor: '#E5EFFF',
//                             display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
//                         }}>
//                             <img src="/icons/history-fill-blue.svg" alt="Previous Chats" style={{ width: '28px', height: '28px' }} />
//                         </Box>
//                         <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '18px', color: '#333333' }}>
//                             Previous Chats
//                         </Typography>
//                     </Box>
                    
//                 </Box>
//                  <Box sx={{ px: 3, pb: 2 }}>
//                     <TextField
//                         fullWidth
//                         variant="outlined"
//                         size="small"
//                         placeholder="Search chats"
//                         value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                         InputProps={{
//                             startAdornment: <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>,
//                             sx: {
//                       fontFamily:'Inter',
//                       fontWeight: 400,
//                       fontSize: '14px',
//                       borderRadius: '8px',
//                       bgcolor: '#F9F9F9',
                      
//                       height:'auto',
//                       '& .MuiOutlinedInput-notchedOutline': {
//                           borderColor: '#E0E0E0'
//                       },
//                       '&:hover .MuiOutlinedInput-notchedOutline': {
//                           borderColor: '#0064FF'
//                       },
//                       '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//                           borderColor: '#0064FF'
//                       }
//                     }
//                         }}
//                     />
//                 </Box>
//                 <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
//                     {renderContent()}
//                 </Box>
//             </Paper>
//         </Modal>
//     );
// };


// // --- END: CHAT HISTORY DIALOG COMPONENT ---


// const gradientAnimation = keyframes`
//   0% { background-position: 0% 50%; }
//   50% { background-position: 100% 50%; }
//   100% { background-position: 0% 50%; }
// `;

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
//   hasEvidence: boolean;
//   rawMessage: ChatHistoryMessage;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: { type: string, k8s_object: string, data: string }[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   userProfilePicture?: string | undefined;
//   onBack: () => void;
//   onMessageSelect: (evidences: Evidence[]) => void;
//   initialPrompt: string | null;
//   onInitialPromptPosted: () => void;
//   previousChats: ChatSession[];
//   // --- ADDED: Prop to handle navigating to a different chat ---
//   onNavigateToChat: (chatId: string) => void;
// }

// interface CodeComponentProps {
//   node?: any;
//   inline?: boolean;
//   className?: string;
//   children?: React.ReactNode;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const mapApiTypeToUiType = (apiType: string = 'unknown'): Evidence['type'] => {
//     const type = apiType.toLowerCase();
//     switch (type) {
//         case 'log':
//         case 'metrics':
//         case 'text':
//             return type;
//         case 'yaml':
//             return 'code';
//         case 'json':
//           return 'log';
//         default:
//             return 'unknown';
//     }
// }

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceItems = msg.metadata?.evidence;
//     if (!evidenceItems || !Array.isArray(evidenceItems) || evidenceItems.length === 0) {
//         return [];
//     }
//     return evidenceItems.map((item, index) => {
//         let details = item.data;
//         let resource = item.k8s_object || 'Details';
//         try {
//             const parsed = JSON.parse(item.data);
//             details = JSON.stringify(parsed, null, 2);
//             if (resource === 'Details' && parsed.name) {
//                 resource = parsed.name;
//             }
//         } catch (e) { /* Not a JSON string, use raw data */ }
        
//         return {
//             id: `${msg.id}-evidence-${index}`,
//             type: mapApiTypeToUiType(item.type),
//             project: 'N/A',
//             resource: resource,
//             timestamp: msg.createdAt,
//             status: 'Normal',
//             details,
//         };
//     });
// };


// const ChatDetailView: FC<ChatDetailViewProps> = ({ chatId, userName, userProfilePicture,onBack, onMessageSelect, initialPrompt, onInitialPromptPosted, previousChats, onNavigateToChat }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  
//   // --- MODIFIED: This now calls the new prop from the parent to change the active chat ---
//   const handleSelectChatFromDialog = (newChatId: string) => {
//       setIsHistoryOpen(false);
//       onNavigateToChat(newChatId); 
//   };
  
//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'Copilot',
//     hasEvidence: !!msg.metadata?.evidence?.length,
//     rawMessage: msg,
//   });

//   const { loading: isLoadingHistory, error: historyError, data } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     fetchPolicy: 'network-only'
//   });

//    useEffect(() => {
//     if (data?.getChatHistory) {
//       let processedHistory: ChatHistoryMessage[] = [...data.getChatHistory];
//       if (processedHistory.length > 2 && processedHistory[0].role === 'user' && processedHistory[2].role === 'agent') {
//          processedHistory = processedHistory.filter((_, index) => index !== 0 && index !== 2);
//       }
//       const historyMessages = processedHistory.map(mapApiMessageToChatMessage);
//       setMessages(historyMessages);
//     }
//   }, [data]);


//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onMessageSelect(newEvidences);
//         }
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         setMessages(prev => prev.filter(m => m.id !== `temp-user-${Date.now()}`));
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: initialPrompt, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId, postMessage, userName]);

//   const handleMessageClick = (message: ChatMessage) => {
//     if (message.sender !== 'agent') {
//       return;
//     }
//     if(selectedMessageId === message.id) {
//       setSelectedMessageId(null);
//       onMessageSelect([]);
//       return;
//     }
//     setSelectedMessageId(message.id);
//     const parsedEvidences = parseAndMapEvidence(message.rawMessage);
//     onMessageSelect(parsedEvidences);
//   }

//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: userMessageText, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };

//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     const baseFontStyles = { fontFamily: 'Inter', fontSize: '14px' };

//     return (
//       <Box sx={baseFontStyles}>
//         <ReactMarkdown
//           remarkPlugins={[remarkGfm]}
//           components={{
//             p: ({ node, children }) => (<Typography component="p" sx={{ fontSize: 'inherit', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{children}</Typography>),
//             h1: ({ node, children }) => <Typography variant="h6" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//             h2: ({ node, children }) => <Typography variant="h6" component="h2" sx={{ fontSize: '1.15rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//             h3: ({ node, children }) => <Typography variant="h6" component="h3" sx={{ fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//             ul: ({ node, children }) => <Box component="ul" sx={{ pl: 3, my: 1 }}>{children}</Box>,
//             ol: ({ node, children }) => <Box component="ol" sx={{ pl: 3, my: 1 }}>{children}</Box>,
//             li: ({ node, children }) => (<Typography component="li" sx={{ fontSize: 'inherit', mb: 0.5 }}>{children}</Typography>),
//             a: ({ node, ...props }) => (<a {...props} style={{ color: linkColor, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" />),
//             code({ node, inline, className, children, ...props }: CodeComponentProps) {
//               const match = /language-(\w+)/.exec(className || '');
//               return !inline && match ? (
//                 <SyntaxHighlighter style={vscDarkPlus as any} language={match[1]} PreTag="div" >
//                   {String(children).replace(/\n$/, '')}
//                 </SyntaxHighlighter>
//               ) : (
//                 <code {...props} style={{ fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.05)', color: '#333', padding: '2px 5px', borderRadius: '4px' }}>
//                   {children}
//                 </code>
//               );
//             },
//           }}
//         >
//           {text}
//         </ReactMarkdown>
//       </Box>
//     );
//   };

//   return (
//       <Paper elevation={0} sx={{ width:'100%', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%', position: 'relative', overflow: 'hidden' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0, zIndex: 1 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1.5, mr:0.8}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}><img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={() => setIsHistoryOpen(true)}>
//                     <img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//              </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2, zIndex: 1 }}>
//           {isLoadingHistory && <Box sx={{display: 'flex', justifyContent: 'center'}}><CircularProgress /></Box>}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} onClick={() => handleMessageClick(msg)} sx={{ cursor:msg.sender === 'agent' ? 'pointer' : 'default', mb: 3 }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? userProfilePicture : finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ 
//                     bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', 
//                     color: '#333333', p: 1.5, borderRadius: '20px', 
//                     border: selectedMessageId === msg.id ? `1px solid ${theme.palette.primary.main}` : 'transparent', 
//                     width: 'auto', alignSelf: msg.sender === 'agent' ? 'flex-start' : 'flex-end',
//                     maxWidth: '100%', overflowX: 'auto'
//                 }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//              </Box>
//             </Box>
//           ))}
//         </Box>
        
//         <img src="/icons/half-gradient.svg" alt="background gradient" style={{ position: 'absolute', bottom: '-10%', left: '-15%', width: '130%', height: 'auto', objectFit: 'contain', opacity: '60%', zIndex: 0, pointerEvents: 'none' }} />
//        <Box sx={{ mt: 'auto', flexShrink: 0, zIndex: 1 }}>
//             <Paper
//                 elevation={0}
//                 sx={{
//                     p: '1.5px', borderRadius: '16px', bgcolor: 'transparent', minHeight: '64px',
//                     display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
//                     '::before': { 
//                         content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
//                         borderRadius: '16px', padding: '1.5px',
//                         background: 'linear-gradient(92.06deg, rgba(6, 121, 255, 0.15) 7.21%, rgba(215, 77, 18, 0.16) 97.95%)',
//                         backgroundSize: '200% 200%', animation: `${gradientAnimation} 3s linear infinite`,
//                         WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
//                         WebkitMaskComposite: 'xor', maskComposite: 'exclude', zIndex: 0,
//                     },
//                 }}
//             >
//                 <Box sx={{
//                     position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column',
//                     flexGrow: 1, bgcolor: '#FFFFFF', borderRadius: '15px', p: '8px' 
//                 }}>
//                     <TextField
//                         fullWidth multiline variant="standard" placeholder="Enter your prompt here..."
//                         value={input} onChange={(e) => setInput(e.target.value)}
//                         onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend()) }
//                         disabled={isSending || isLoadingHistory}
//                         sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                         InputProps={{ disableUnderline: true, style: { fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' } }}
//                     />
//                     <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
//                         <IconButton
//                             color="primary" onClick={handleSend} disabled={isSending || isLoadingHistory || !input.trim()}
//                             sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                         >
//                             {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                         </IconButton>
//                     </Box>
//                 </Box>
//             </Paper>
//         </Box>
        
//         <ChatHistoryDialog
//             open={isHistoryOpen}
//             onClose={() => setIsHistoryOpen(false)}
//             onSelectChat={handleSelectChatFromDialog}
//             previousChats={previousChats}
//         />
//       </Paper>
//   );
// };

// export default ChatDetailView;






// import React, { useState, useRef, useEffect, FC, useMemo } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
//   Modal,
//   List,
//   ListItem,
//   InputAdornment,
// } from '@mui/material';
// import { Close as CloseIcon } from '@mui/icons-material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel';
// import ReactMarkdown from 'react-markdown';
// import remarkGfm from 'remark-gfm';
// import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
// import { keyframes } from '@emotion/react';


// // --- START: CHAT HISTORY DIALOG COMPONENT ---
// // This component now receives the chat list as a prop, removing its own data fetching.

// const YOUR_TEXTURE_SVG_PATH = '/textures/Texture.svg';
// const YOUR_GRADIENT_SVG_PATH = '/images/Gradient_Popup.svg';

// interface ChatSession {
//   id: string;
//   title: string;
//   createdAt: string;
// }

// interface ChatHistoryDialogProps {
//   open: boolean;
//   onClose: () => void;
//   onSelectChat: (chatId: string) => void;
//   // ADDED: Prop to receive chats from the parent
//   previousChats: ChatSession[];
// }

// const groupChatsByDate = (chats: ChatSession[]): Record<string, ChatSession[]> => {
//   const groups: Record<string, ChatSession[]> = { Today: [], Yesterday: [], "Last 7 days": [] };
//   const now = new Date();
//   const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
//   const yesterday = new Date(today);
//   yesterday.setDate(yesterday.getDate() - 1);
//   const last7days = new Date(today);
//   last7days.setDate(last7days.getDate() - 7);

//   chats.forEach(chat => {
//     const chatDate = new Date(chat.createdAt);
//     if (chatDate >= today) {
//       groups.Today.push(chat);
//     } else if (chatDate >= yesterday) {
//       groups.Yesterday.push(chat);
//     } else if (chatDate >= last7days) {
//       groups["Last 7 days"].push(chat);
//     }
//   });

//   return Object.fromEntries(Object.entries(groups).filter(([_, value]) => value.length > 0));
// };

// const ChatHistoryDialog: FC<ChatHistoryDialogProps> = ({ open, onClose, onSelectChat, previousChats }) => {
//     const [searchTerm, setSearchTerm] = useState('');

//     const filteredAndGroupedChats = useMemo(() => {
//         if (!previousChats) return {};
//         const filtered = previousChats.filter((chat: ChatSession) =>
//             chat.title.toLowerCase().includes(searchTerm.toLowerCase())
//         );
//         return groupChatsByDate(filtered);
//     }, [previousChats, searchTerm]);

//     const renderContent = () => {
//         if (!previousChats) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
//         if (Object.keys(filteredAndGroupedChats).length === 0 && searchTerm) return <Typography sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>No chats match your search.</Typography>;
//         if (Object.keys(filteredAndGroupedChats).length === 0) return <Typography sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>No previous chats found.</Typography>;


//         return (
//             <Box>
//                 {Object.entries(filteredAndGroupedChats).map(([group, chats]) => (
//                     <Box key={group} sx={{ mb: 2 }}>
//                         <Typography variant="body2" sx={{ fontWeight: 500, color: '#666666', mb: 1, px: 3 }}>
//                             {group}
//                         </Typography>
//                         <List disablePadding>
//                             {(chats as ChatSession[]).map((chat) => (
//                                 <ListItem
//                                     key={chat.id}
//                                     onClick={() => onSelectChat(chat.id)}
//                                     sx={{
//                                         py: 1, px: 3, cursor: 'pointer',
//                                         '&:hover': { bgcolor: 'action.hover' }
//                                     }}
//                                 >
//                                     <Typography variant="body2" sx={{ color: '#333333', border: '1px dashed #CCCCCC', borderRadius: '20px', p: '8px 14px', width: 'auto' }}>
//                                         {chat.title}
//                                     </Typography>
//                                 </ListItem>
//                             ))}
//                         </List>
//                     </Box>
//                 ))}
//             </Box>
//         );
//     };

//     return (
//         <Modal open={open} onClose={onClose} closeAfterTransition>
//             <Paper sx={{
//                 position: 'absolute', top: 16, right: 16,
//                 width: { xs: '90%', sm: 480 },
//                 height: 'calc(100% - 32px)',
//                 borderRadius: '16px',
//                 boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
//                 bgcolor: '#FFFFFF',
//                 display: 'flex', flexDirection: 'column',
//                 outline: 'none',
//             }}>
//                 <IconButton onClick={onClose} sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
//                     <CloseIcon />
//                 </IconButton>
//                 <Box sx={{
//                     display: 'flex', alignItems: 'center', mb: 2, flexShrink: 0,
//                     backgroundImage: `url(${YOUR_TEXTURE_SVG_PATH}), url(${YOUR_GRADIENT_SVG_PATH})`,
//                     backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center',
//                     borderRadius: '8px', p: 1, minHeight: '120px',
//                 }}>
//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
//                         <Box sx={{
//                             width: 56, height: 56, borderRadius: '10px', backgroundColor: '#E5EFFF',
//                             display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
//                         }}>
//                             <img src="/icons/history-fill.svg" alt="Previous Chats" style={{ width: '28px', height: '28px' }} />
//                         </Box>
//                         <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '18px', color: '#333333' }}>
//                             Previous Chats
//                         </Typography>
//                     </Box>
//                 </Box>
//                  <Box sx={{ px: 3, pb: 2 }}>
//                     <TextField
//                         fullWidth
//                         variant="outlined"
//                         size="small"
//                         placeholder="Search chats"
//                         value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                         InputProps={{
//                             startAdornment: <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>,
//                         }}
//                     />
//                 </Box>
//                 <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
//                     {renderContent()}
//                 </Box>
//             </Paper>
//         </Modal>
//     );
// };


// // --- END: CHAT HISTORY DIALOG COMPONENT ---


// const gradientAnimation = keyframes`
//   0% { background-position: 0% 50%; }
//   50% { background-position: 100% 50%; }
//   100% { background-position: 0% 50%; }
// `;

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
//   hasEvidence: boolean;
//   rawMessage: ChatHistoryMessage;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: { type: string, k8s_object: string, data: string }[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   userProfilePicture?: string | undefined;
//   onBack: () => void;
//   onMessageSelect: (evidences: Evidence[]) => void;
//   initialPrompt: string | null;
//   onInitialPromptPosted: () => void;
//   // ADDED: Prop to receive the list of all chats
//   previousChats: ChatSession[];
// }

// interface CodeComponentProps {
//   node?: any;
//   inline?: boolean;
//   className?: string;
//   children?: React.ReactNode;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const mapApiTypeToUiType = (apiType: string = 'unknown'): Evidence['type'] => {
//     const type = apiType.toLowerCase();
//     switch (type) {
//         case 'log':
//         case 'metrics':
//         case 'text':
//             return type;
//         case 'yaml':
//             return 'code';
//         case 'json':
//           return 'log';
//         default:
//             return 'unknown';
//     }
// }

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceItems = msg.metadata?.evidence;
//     if (!evidenceItems || !Array.isArray(evidenceItems) || evidenceItems.length === 0) {
//         return [];
//     }
//     return evidenceItems.map((item, index) => {
//         let details = item.data;
//         let resource = item.k8s_object || 'Details';
//         try {
//             const parsed = JSON.parse(item.data);
//             details = JSON.stringify(parsed, null, 2);
//             if (resource === 'Details' && parsed.name) {
//                 resource = parsed.name;
//             }
//         } catch (e) { /* Not a JSON string, use raw data */ }
        
//         return {
//             id: `${msg.id}-evidence-${index}`,
//             type: mapApiTypeToUiType(item.type),
//             project: 'N/A',
//             resource: resource,
//             timestamp: msg.createdAt,
//             status: 'Normal',
//             details,
//         };
//     });
// };


// const ChatDetailView: FC<ChatDetailViewProps> = ({ chatId, userName, userProfilePicture,onBack, onMessageSelect, initialPrompt, onInitialPromptPosted, previousChats }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const [isHistoryOpen, setIsHistoryOpen] = useState(false);
//   const handleSelectChatFromDialog = (newChatId: string) => {
//       setIsHistoryOpen(false);
//       // To select a different chat, we must go back to the parent page,
//       // which can then set the new active chat ID.
//       onBack(); 
//   };
  
//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'Copilot',
//     hasEvidence: !!msg.metadata?.evidence?.length,
//     rawMessage: msg,
//   });

//   const { loading: isLoadingHistory, error: historyError, data } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     fetchPolicy: 'network-only'
//   });

//    useEffect(() => {
//     if (data?.getChatHistory) {
//       let processedHistory: ChatHistoryMessage[] = [...data.getChatHistory];
//       if (processedHistory.length > 2 && processedHistory[0].role === 'user' && processedHistory[2].role === 'agent') {
//          processedHistory = processedHistory.filter((_, index) => index !== 0 && index !== 2);
//       }
//       const historyMessages = processedHistory.map(mapApiMessageToChatMessage);
//       setMessages(historyMessages);
//     }
//   }, [data]);


//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onMessageSelect(newEvidences);
//         }
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         setMessages(prev => prev.filter(m => m.id !== `temp-user-${Date.now()}`));
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: initialPrompt, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId, postMessage, userName]);

//   const handleMessageClick = (message: ChatMessage) => {
//     if (message.sender !== 'agent') {
//       return;
//     }
//     if(selectedMessageId === message.id) {
//       setSelectedMessageId(null);
//       onMessageSelect([]);
//       return;
//     }
//     setSelectedMessageId(message.id);
//     const parsedEvidences = parseAndMapEvidence(message.rawMessage);
//     onMessageSelect(parsedEvidences);
//   }

//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: userMessageText, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };

//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     const baseFontStyles = { fontFamily: 'Inter', fontSize: '14px' };

//     return (
//       <Box sx={baseFontStyles}>
//         <ReactMarkdown
//           remarkPlugins={[remarkGfm]}
//           components={{
//             p: ({ node, children }) => (<Typography component="p" sx={{ fontSize: 'inherit', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{children}</Typography>),
//             h1: ({ node, children }) => <Typography variant="h6" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//             h2: ({ node, children }) => <Typography variant="h6" component="h2" sx={{ fontSize: '1.15rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//             h3: ({ node, children }) => <Typography variant="h6" component="h3" sx={{ fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//             ul: ({ node, children }) => <Box component="ul" sx={{ pl: 3, my: 1 }}>{children}</Box>,
//             ol: ({ node, children }) => <Box component="ol" sx={{ pl: 3, my: 1 }}>{children}</Box>,
//             li: ({ node, children }) => (<Typography component="li" sx={{ fontSize: 'inherit', mb: 0.5 }}>{children}</Typography>),
//             a: ({ node, ...props }) => (<a {...props} style={{ color: linkColor, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" />),
//             code({ node, inline, className, children, ...props }: CodeComponentProps) {
//               const match = /language-(\w+)/.exec(className || '');
//               return !inline && match ? (
//                 <SyntaxHighlighter style={vscDarkPlus as any} language={match[1]} PreTag="div" >
//                   {String(children).replace(/\n$/, '')}
//                 </SyntaxHighlighter>
//               ) : (
//                 <code {...props} style={{ fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.05)', color: '#333', padding: '2px 5px', borderRadius: '4px' }}>
//                   {children}
//                 </code>
//               );
//             },
//           }}
//         >
//           {text}
//         </ReactMarkdown>
//       </Box>
//     );
//   };

//   return (
//       <Paper elevation={0} sx={{ width:'100%', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%', position: 'relative', overflow: 'hidden' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0, zIndex: 1 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1.5, mr:0.8}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}><img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={() => setIsHistoryOpen(true)}>
//                     <img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//              </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2, zIndex: 1 }}>
//           {isLoadingHistory && <Box sx={{display: 'flex', justifyContent: 'center'}}><CircularProgress /></Box>}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} onClick={() => handleMessageClick(msg)} sx={{ cursor:msg.sender === 'agent' ? 'pointer' : 'default', mb: 3 }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? userProfilePicture : finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ 
//                     bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', 
//                     color: '#333333', p: 1.5, borderRadius: '20px', 
//                     border: selectedMessageId === msg.id ? `1px solid ${theme.palette.primary.main}` : 'transparent', 
//                     width: 'auto', alignSelf: msg.sender === 'agent' ? 'flex-start' : 'flex-end',
//                     maxWidth: '100%', overflowX: 'auto'
//                 }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//              </Box>
//             </Box>
//           ))}
//         </Box>
        
//         <img src="/icons/half-gradient.svg" alt="background gradient" style={{ position: 'absolute', bottom: '-10%', left: '-15%', width: '130%', height: 'auto', objectFit: 'contain', opacity: '60%', zIndex: 0, pointerEvents: 'none' }} />
//        <Box sx={{ mt: 'auto', flexShrink: 0, zIndex: 1 }}>
//             <Paper
//                 elevation={0}
//                 sx={{
//                     p: '1.5px', borderRadius: '16px', bgcolor: 'transparent', minHeight: '64px',
//                     display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
//                     '::before': { 
//                         content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
//                         borderRadius: '16px', padding: '1.5px',
//                         background: 'linear-gradient(92.06deg, rgba(6, 121, 255, 0.15) 7.21%, rgba(215, 77, 18, 0.16) 97.95%)',
//                         backgroundSize: '200% 200%', animation: `${gradientAnimation} 3s linear infinite`,
//                         WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
//                         WebkitMaskComposite: 'xor', maskComposite: 'exclude', zIndex: 0,
//                     },
//                 }}
//             >
//                 <Box sx={{
//                     position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column',
//                     flexGrow: 1, bgcolor: '#FFFFFF', borderRadius: '15px', p: '8px' 
//                 }}>
//                     <TextField
//                         fullWidth multiline variant="standard" placeholder="Enter your prompt here..."
//                         value={input} onChange={(e) => setInput(e.target.value)}
//                         onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend()) }
//                         disabled={isSending || isLoadingHistory}
//                         sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                         InputProps={{ disableUnderline: true, style: { fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' } }}
//                     />
//                     <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
//                         <IconButton
//                             color="primary" onClick={handleSend} disabled={isSending || isLoadingHistory || !input.trim()}
//                             sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                         >
//                             {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                         </IconButton>
//                     </Box>
//                 </Box>
//             </Paper>
//         </Box>
        
//         <ChatHistoryDialog
//             open={isHistoryOpen}
//             onClose={() => setIsHistoryOpen(false)}
//             onSelectChat={handleSelectChatFromDialog}
//             previousChats={previousChats}
//         />
//       </Paper>
//   );
// };

// export default ChatDetailView;





// import React, { useState, useRef, useEffect, FC, CSSProperties } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel';
// import ReactMarkdown from 'react-markdown';
// import remarkGfm from 'remark-gfm';
// import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
// import { keyframes } from '@emotion/react';
// const gradientAnimation = keyframes`
//   0% { background-position: 0% 50%; }
//   50% { background-position: 100% 50%; }
//   100% { background-position: 0% 50%; }
// `;

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
//   hasEvidence: boolean;
//   rawMessage: ChatHistoryMessage;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: { type: string, k8s_object: string, data: string }[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   userProfilePicture?: string | undefined;
//   onBack: () => void;
//   onMessageSelect: (evidences: Evidence[]) => void;
//   initialPrompt: string | null;
//   onInitialPromptPosted: () => void;
// }

// // FIX: Define the props for the custom code component to satisfy TypeScript
// interface CodeComponentProps {
//   node?: any;
//   inline?: boolean;
//   className?: string;
//   children?: React.ReactNode;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const mapApiTypeToUiType = (apiType: string = 'unknown'): Evidence['type'] => {
//     const type = apiType.toLowerCase();
//     switch (type) {
//         case 'log':
//         case 'metrics':
//         case 'text':
//             return type;
//         case 'yaml':
//             return 'code';
//         case 'json':
//           return 'log';
//         default:
//             return 'unknown';
//     }
// }

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceItems = msg.metadata?.evidence;
//     if (!evidenceItems || !Array.isArray(evidenceItems) || evidenceItems.length === 0) {
//         return [];
//     }
//     return evidenceItems.map((item, index) => {
//         let details = item.data;
//         let resource = item.k8s_object || 'Details';
//         try {
//             const parsed = JSON.parse(item.data);
//             details = JSON.stringify(parsed, null, 2);
//             if (resource === 'Details' && parsed.name) {
//                 resource = parsed.name;
//             }
//         } catch (e) { /* Not a JSON string, use raw data */ }
        
//         return {
//             id: `${msg.id}-evidence-${index}`,
//             type: mapApiTypeToUiType(item.type),
//             project: 'N/A',
//             resource: resource,
//             timestamp: msg.createdAt,
//             status: 'Normal',
//             details,
//         };
//     });
// };


// const ChatDetailView: FC<ChatDetailViewProps> = ({ chatId, userName, userProfilePicture,onBack, onMessageSelect, initialPrompt, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const finOpsIconUrl = '/images/Chatbot.svg';
  
//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'Copilot',
//     hasEvidence: !!msg.metadata?.evidence?.length,
//     rawMessage: msg,
//   });

//   const { loading: isLoadingHistory, error: historyError, data } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     fetchPolicy: 'network-only'
//   });

//    useEffect(() => {
//     if (data?.getChatHistory) {
//       let processedHistory: ChatHistoryMessage[] = [...data.getChatHistory];
//       if (processedHistory.length > 2 && processedHistory[0].role === 'user' && processedHistory[2].role === 'agent') {
//          processedHistory = processedHistory.filter((_, index) => index !== 0 && index !== 2);
//       }
//       const historyMessages = processedHistory.map(mapApiMessageToChatMessage);
//       setMessages(historyMessages);
//     }
//   }, [data]);


//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onMessageSelect(newEvidences);
//         }
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         setMessages(prev => prev.filter(m => m.id !== `temp-user-${Date.now()}`));
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: initialPrompt, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId, postMessage, userName]);

//   const handleMessageClick = (message: ChatMessage) => {
//     if (message.sender !== 'agent') {
//       return;
//     }
//     if(selectedMessageId === message.id) {
//       setSelectedMessageId(null);
//       onMessageSelect([]);
//       return;
//     }
//     setSelectedMessageId(message.id);
//     const parsedEvidences = parseAndMapEvidence(message.rawMessage);
//     onMessageSelect(parsedEvidences);
//   }

//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: userMessageText, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//   const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;

//   // Define your desired font styles here
//   const baseFontStyles = {
//     fontFamily: 'Inter',
//     fontSize: '14px', // This will be our base font size
//   };

//   return (
//     <Box sx={baseFontStyles}>
//       <ReactMarkdown
//         remarkPlugins={[remarkGfm]}
//         components={{
//           p: ({ node, children }) => (
//             <Typography component="p" sx={{ fontSize: 'inherit', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
//               {children}
//             </Typography>
//           ),
          
//           h1: ({ node, children }) => <Typography variant="h6" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//           h2: ({ node, children }) => <Typography variant="h6" component="h2" sx={{ fontSize: '1.15rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//           h3: ({ node, children }) => <Typography variant="h6" component="h3" sx={{ fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//           h4: ({ node, children }) => <Typography variant="h6" component="h4" sx={{ fontSize: '1rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//           h5: ({ node, children }) => <Typography variant="h6" component="h5" sx={{ fontSize: '1rem', fontWeight: 600 }}>{children}</Typography>,
//           h6: ({ node, children }) => <Typography variant="h6" component="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>{children}</Typography>,

//           // FIX: Destructure children and stop spreading `...props` to avoid the 'ref' conflict.
//           ul: ({ node, children }) => <Box component="ul" sx={{ pl: 3, my: 1 }}>{children}</Box>,
//           ol: ({ node, children }) => <Box component="ol" sx={{ pl: 3, my: 1 }}>{children}</Box>,

//           li: ({ node, children }) => (
//             <Typography component="li" sx={{ fontSize: 'inherit', mb: 0.5 }}>{children}</Typography>
//           ),
          
//           a: ({ node, ...props }) => (
//             <a {...props} style={{ color: linkColor, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" />
//           ),

//           code({ node, inline, className, children, ...props }: CodeComponentProps) {
//             const match = /language-(\w+)/.exec(className || '');
//             return !inline && match ? (
//               <SyntaxHighlighter style={vscDarkPlus as any} language={match[1]} PreTag="div" >
//                 {String(children).replace(/\n$/, '')}
//               </SyntaxHighlighter>
//             ) : (
//               <code {...props} style={{ fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.05)', color: '#333', padding: '2px 5px', borderRadius: '4px' }}>
//                 {children}
//               </code>
//             );
//           },
//         }}
//       >
//         {text}
//       </ReactMarkdown>
//     </Box>
//   );
// };

//   return (
//       <Paper elevation={0} sx={{ 
//         width:'100%',
//         display: 'flex', 
//         flexDirection: 'column', 
//         p: 2.5, 
//         bgcolor: 'white', 
//         borderRadius: '12px', 
//         height: '100%',
//         position: 'relative',
//         overflow: 'hidden'
//       }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0, zIndex: 1 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1.5, mr:0.8}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}><img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} /></IconButton>
//              </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2, zIndex: 1 }}>
//           {isLoadingHistory && <Box sx={{display: 'flex', justifyContent: 'center'}}><CircularProgress /></Box>}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} onClick={() => handleMessageClick(msg)} sx={{ cursor:msg.sender === 'agent' ? 'pointer' : 'default', mb: 3 }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? userProfilePicture : finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ 
//                     bgcolor: msg.sender === 'user' ? '#F2F7FF' : (selectedMessageId === msg.id ? '#FFFFFF' : '#FFFFFF'), 
//                     color: '#333333', 
//                     p: 1.5, 
//                     borderRadius: '20px', 
//                     border: selectedMessageId === msg.id ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent', 
//                     width: 'auto', 
//                     alignSelf: msg.sender === 'agent' ? 'flex-start' : 'flex-end',
//                     maxWidth: '100%',
//                     overflowX: 'auto'
//                 }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//              </Box>
//             </Box>
//           ))}
//         </Box>
        
//         <img 
//             src="/icons/half-gradient.svg" 
//             alt="background gradient" 
//             style={{ 
//                 position: 'absolute', 
//                 bottom: '-10%',
//                 left: '-15%',
//                 width: '130%',
//                 height: 'auto',
//                 objectFit: 'contain', 
//                 opacity: '60%', 
//                 zIndex: 0, 
//                 pointerEvents: 'none'
//             }} 
//         />
//        <Box sx={{ mt: 'auto', flexShrink: 0, zIndex: 1 }}>
//     <Paper
//         elevation={0}
//         sx={{
//             p: '1.5px', 
//             borderRadius: '16px',
//             bgcolor: 'transparent',
//             minHeight: '64px',
//             display: 'flex',
//             flexDirection: 'column',
//             position: 'relative',
//             overflow: 'hidden',
//             '::before': { 
//                 content: '""',
//                 position: 'absolute',
//                 top: 0,
//                 left: 0,
//                 right: 0,
//                 bottom: 0,
//                 borderRadius: '16px',
//                 padding: '1.5px',
//                 background: 'linear-gradient(92.06deg, rgba(6, 121, 255, 0.15) 7.21%, rgba(215, 77, 18, 0.16) 97.95%)',
//                 backgroundSize: '200% 200%',
//                 animation: `${gradientAnimation} 3s linear infinite`,
//                 WebkitMask:
//                     'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
//                 WebkitMaskComposite: 'xor',
//                 maskComposite: 'exclude',
//                 zIndex: 0,
//             },
//         }}
//     >
//         <Box sx={{
//             position: 'relative',
//             zIndex: 1,
//             display: 'flex',
//             flexDirection: 'column',
//             flexGrow: 1,
//             bgcolor: '#FFFFFF', 
//             borderRadius: '15px', 
//             p: '8px' 
//         }}>
//             <TextField
//                 fullWidth
//                 multiline
//                 variant="standard"
//                 placeholder="Enter your prompt here..."
//                 value={input}
//                 onChange={(e) => setInput(e.target.value)}
//                 onKeyPress={(e) =>
//                     e.key === 'Enter' &&
//                     !e.shiftKey &&
//                     !isSending &&
//                     (e.preventDefault(), handleSend())
//                 }
//                 disabled={isSending || isLoadingHistory}
//                 sx={{
//                     flexGrow: 1,
//                     height: '100%',
//                     '& .MuiInputBase-root': { height: '100%' },
//                 }}
//                 InputProps={{
//                     disableUnderline: true,
//                     style: { fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' },
//                 }}
//             />
//             <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
//                 <IconButton
//                     color="primary"
//                     onClick={handleSend}
//                     disabled={isSending || isLoadingHistory || !input.trim()}
//                     sx={{
//                         bgcolor: '#0064FF',
//                         color: 'white',
//                         width: '32px',
//                         height: '32px',
//                         '&:hover': { bgcolor: '#0052cc' },
//                         '&.Mui-disabled': { bgcolor: 'grey.300' },
//                     }}
//                 >
//                     {isSending ? (
//                         <CircularProgress size={16} color="inherit" />
//                     ) : (
//                         <img src="/icons/send.svg" alt="send" />
//                     )}
//                 </IconButton>
//             </Box>
//         </Box>
//     </Paper>
// </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;





// import React, { useState, useRef, useEffect, FC, CSSProperties } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel';
// import ReactMarkdown from 'react-markdown';
// import remarkGfm from 'remark-gfm';
// import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
// import { keyframes } from '@emotion/react';
// const gradientAnimation = keyframes`
//   0% { background-position: 0% 50%; }
//   50% { background-position: 100% 50%; }
//   100% { background-position: 0% 50%; }
// `;

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
//   hasEvidence: boolean;
//   rawMessage: ChatHistoryMessage;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: { type: string, k8s_object: string, data: string }[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   userProfilePicture?: string | undefined;
//   onBack: () => void;
//   onMessageSelect: (evidences: Evidence[]) => void;
//   initialPrompt: string | null;
//   onInitialPromptPosted: () => void;
// }

// // FIX: Define the props for the custom code component to satisfy TypeScript
// interface CodeComponentProps {
//   node?: any;
//   inline?: boolean;
//   className?: string;
//   children?: React.ReactNode;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const mapApiTypeToUiType = (apiType: string = 'unknown'): Evidence['type'] => {
//     const type = apiType.toLowerCase();
//     switch (type) {
//         case 'log':
//         case 'metrics':
//         case 'text':
//             return type;
//         case 'yaml':
//             return 'code';
//         default:
//             return 'unknown';
//     }
// }

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceItems = msg.metadata?.evidence;
//     if (!evidenceItems || !Array.isArray(evidenceItems) || evidenceItems.length === 0) {
//         return [];
//     }
//     return evidenceItems.map((item, index) => {
//         let details = item.data;
//         let resource = item.k8s_object || 'Details';
//         try {
//             const parsed = JSON.parse(item.data);
//             details = JSON.stringify(parsed, null, 2);
//             if (resource === 'Details' && parsed.name) {
//                 resource = parsed.name;
//             }
//         } catch (e) { /* Not a JSON string, use raw data */ }
        
//         return {
//             id: `${msg.id}-evidence-${index}`,
//             type: mapApiTypeToUiType(item.type),
//             project: 'N/A',
//             resource: resource,
//             timestamp: msg.createdAt,
//             status: 'Normal',
//             details,
//         };
//     });
// };


// const ChatDetailView: FC<ChatDetailViewProps> = ({ chatId, userName, userProfilePicture,onBack, onMessageSelect, initialPrompt, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const finOpsIconUrl = '/images/Chatbot.svg';
  
//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'Copilot',
//     hasEvidence: !!msg.metadata?.evidence?.length,
//     rawMessage: msg,
//   });

//   const { loading: isLoadingHistory, error: historyError, data } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     fetchPolicy: 'network-only'
//   });

//    useEffect(() => {
//     if (data?.getChatHistory) {
//       let processedHistory: ChatHistoryMessage[] = [...data.getChatHistory];
//       if (processedHistory.length > 2 && processedHistory[0].role === 'user' && processedHistory[2].role === 'agent') {
//          processedHistory = processedHistory.filter((_, index) => index !== 0 && index !== 2);
//       }
//       const historyMessages = processedHistory.map(mapApiMessageToChatMessage);
//       setMessages(historyMessages);
//     }
//   }, [data]);


//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onMessageSelect(newEvidences);
//         }
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         setMessages(prev => prev.filter(m => m.id !== `temp-user-${Date.now()}`));
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: initialPrompt, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId, postMessage, userName]);

//   const handleMessageClick = (message: ChatMessage) => {
//     if (message.sender !== 'agent') {
//       return;
//     }
//     if(selectedMessageId === message.id) {
//       setSelectedMessageId(null);
//       onMessageSelect([]);
//       return;
//     }
//     setSelectedMessageId(message.id);
//     const parsedEvidences = parseAndMapEvidence(message.rawMessage);
//     onMessageSelect(parsedEvidences);
//   }

//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: userMessageText, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//   const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;

//   // Define your desired font styles here
//   const baseFontStyles = {
//     fontFamily: 'Inter',
//     fontSize: '14px', // This will be our base font size
//   };

//   return (
//     <Box sx={baseFontStyles}>
//       <ReactMarkdown
//         remarkPlugins={[remarkGfm]}
//         components={{
//           p: ({ node, children }) => (
//             <Typography component="p" sx={{ fontSize: 'inherit', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
//               {children}
//             </Typography>
//           ),
          
//           h1: ({ node, children }) => <Typography variant="h6" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//           h2: ({ node, children }) => <Typography variant="h6" component="h2" sx={{ fontSize: '1.15rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//           h3: ({ node, children }) => <Typography variant="h6" component="h3" sx={{ fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//           h4: ({ node, children }) => <Typography variant="h6" component="h4" sx={{ fontSize: '1rem', fontWeight: 600, mt: 2, mb: 1 }}>{children}</Typography>,
//           h5: ({ node, children }) => <Typography variant="h6" component="h5" sx={{ fontSize: '1rem', fontWeight: 600 }}>{children}</Typography>,
//           h6: ({ node, children }) => <Typography variant="h6" component="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>{children}</Typography>,

//           // FIX: Destructure children and stop spreading `...props` to avoid the 'ref' conflict.
//           ul: ({ node, children }) => <Box component="ul" sx={{ pl: 3, my: 1 }}>{children}</Box>,
//           ol: ({ node, children }) => <Box component="ol" sx={{ pl: 3, my: 1 }}>{children}</Box>,

//           li: ({ node, children }) => (
//             <Typography component="li" sx={{ fontSize: 'inherit', mb: 0.5 }}>{children}</Typography>
//           ),
          
//           a: ({ node, ...props }) => (
//             <a {...props} style={{ color: linkColor, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" />
//           ),

//           code({ node, inline, className, children, ...props }: CodeComponentProps) {
//             const match = /language-(\w+)/.exec(className || '');
//             return !inline && match ? (
//               <SyntaxHighlighter style={vscDarkPlus as any} language={match[1]} PreTag="div" >
//                 {String(children).replace(/\n$/, '')}
//               </SyntaxHighlighter>
//             ) : (
//               <code {...props} style={{ fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.05)', color: '#333', padding: '2px 5px', borderRadius: '4px' }}>
//                 {children}
//               </code>
//             );
//           },
//         }}
//       >
//         {text}
//       </ReactMarkdown>
//     </Box>
//   );
// };
//   // const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//   //   const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;

//   //   return (
//   //     <ReactMarkdown
//   //       remarkPlugins={[remarkGfm]}
//   //       components={{
//   //         p: ({node, children}) => <Typography component="p" sx={{ fontFamily:'Inter', lineHeight: 1.6, fontSize: '14px',  fontWeight: 500, whiteSpace: 'pre-wrap' }}>{children}</Typography>,
//   //         a: ({node, ...props}) => <a {...props} style={{ color: linkColor, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" />,
          
//   //         // FIX: Apply the custom interface to the function arguments
//   //         code({ node, inline, className, children, ...props }: CodeComponentProps) {
//   //           const match = /language-(\w+)/.exec(className || '');
//   //           return !inline && match ? (
//   //             <SyntaxHighlighter
//   //               style={vscDarkPlus as any}
//   //               language={match[1]}
//   //               PreTag="div"
//   //             >
//   //               {String(children).replace(/\n$/, '')}
//   //             </SyntaxHighlighter>
//   //           ) : (
//   //             <code {...props} style={{ fontFamily: 'Inter', backgroundColor: 'rgba(0,0,0,0.05)', color: '#333', padding: '2px 5px', borderRadius: '4px', fontSize: '0.9em'}}>
//   //               {children}
//   //             </code>
//   //           );
//   //         }
//   //       }}
//   //     >
//   //       {text}
//   //     </ReactMarkdown>
//   //   );
//   // };

//   return (
//       <Paper elevation={0} sx={{ 
//         width:'100%',
//         display: 'flex', 
//         flexDirection: 'column', 
//         p: 2.5, 
//         bgcolor: 'white', 
//         borderRadius: '12px', 
//         height: '100%',
//         position: 'relative',
//         overflow: 'hidden'
//       }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0, zIndex: 1 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}><img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//                {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box> */}
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2, zIndex: 1 }}>
//           {isLoadingHistory && <Box sx={{display: 'flex', justifyContent: 'center'}}><CircularProgress /></Box>}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} onClick={() => handleMessageClick(msg)} sx={{ cursor:msg.sender === 'agent' ? 'pointer' : 'default', mb: 3 }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? userProfilePicture : finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : (selectedMessageId === msg.id ? '#FFFFFF' : '#FFFFFF'), color: '#333333', p: 1.5, borderRadius: '20px', border: selectedMessageId === msg.id ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent', width: msg.sender === 'agent' ? 'auto' : 'auto', alignSelf: msg.sender === 'agent' ? 'flex-start' : 'flex-end' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {/* {msg.sender === 'agent' && ( <Box sx={{ mt: 1 }}><IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton><IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton></Box>)} */}
//              </Box>
//             </Box>
//           ))}
//         </Box>
        
//         <img 
//             src="/icons/half-gradient.svg" 
//             alt="background gradient" 
//             style={{ 
//                 position: 'absolute', 
//                 bottom: '-10%',
//                 left: '-15%',
//                 width: '130%',
//                 height: 'auto',
//                 objectFit: 'contain', 
//                 opacity: '60%', 
//                 zIndex: 0, 
//                 pointerEvents: 'none'
//             }} 
//         />
//        <Box sx={{ mt: 'auto', flexShrink: 0, zIndex: 1 }}>
//     {/* The main changes are in this Paper component's sx prop */}
//     <Paper
//         elevation={0}
//         sx={{
//             p: '1.5px', // This acts as the border thickness
//             borderRadius: '16px',
//             bgcolor: 'transparent', // The container itself is transparent
//             minHeight: '64px',
//             display: 'flex',
//             flexDirection: 'column',
//             position: 'relative',
//             overflow: 'hidden',
//             '::before': { // A pseudo-element creates the animated gradient border
//                 content: '""',
//                 position: 'absolute',
//                 top: 0,
//                 left: 0,
//                 right: 0,
//                 bottom: 0,
//                 borderRadius: '16px',
//                 padding: '1.5px',
//                 // Your custom gradient from Figma is used here
//                 background: 'linear-gradient(92.06deg, rgba(6, 121, 255, 0.15) 7.21%, rgba(215, 77, 18, 0.16) 97.95%)',
//                 backgroundSize: '200% 200%',
//                 // The animation is applied here
//                 animation: `${gradientAnimation} 3s linear infinite`,
//                 // Masking is used to create the border effect
//                 WebkitMask:
//                     'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
//                 WebkitMaskComposite: 'xor',
//                 maskComposite: 'exclude',
//                 zIndex: 0,
//             },
//         }}
//     >
//         {/* This inner Box now holds the content and white background */}
//         <Box sx={{
//             position: 'relative',
//             zIndex: 1,
//             display: 'flex',
//             flexDirection: 'column',
//             flexGrow: 1,
//             bgcolor: '#FFFFFF', // White background for the content area
//             borderRadius: '15px', // Slightly smaller radius to fit inside the border
//             p: '8px' // Original padding is applied here now
//         }}>
//             <TextField
//                 fullWidth
//                 multiline
//                 variant="standard"
//                 placeholder="Enter your prompt here..."
//                 value={input}
//                 onChange={(e) => setInput(e.target.value)}
//                 onKeyPress={(e) =>
//                     e.key === 'Enter' &&
//                     !e.shiftKey &&
//                     !isSending &&
//                     (e.preventDefault(), handleSend())
//                 }
//                 disabled={isSending || isLoadingHistory}
//                 sx={{
//                     flexGrow: 1,
//                     height: '100%',
//                     '& .MuiInputBase-root': { height: '100%' },
//                 }}
//                 InputProps={{
//                     disableUnderline: true,
//                     style: { fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' },
//                 }}
//             />
//             <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
//                 <IconButton
//                     color="primary"
//                     onClick={handleSend}
//                     disabled={isSending || isLoadingHistory || !input.trim()}
//                     sx={{
//                         bgcolor: '#0064FF',
//                         color: 'white',
//                         width: '32px',
//                         height: '32px',
//                         '&:hover': { bgcolor: '#0052cc' },
//                         '&.Mui-disabled': { bgcolor: 'grey.300' },
//                     }}
//                 >
//                     {isSending ? (
//                         <CircularProgress size={16} color="inherit" />
//                     ) : (
//                         <img src="/icons/send.svg" alt="send" />
//                     )}
//                 </IconButton>
//             </Box>
//         </Box>
//     </Paper>
// </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;

// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel';

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
//   hasEvidence: boolean;
//   rawMessage: ChatHistoryMessage;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: { type: string, k8s_object: string, data: string }[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   userProfilePicture?: string | undefined;
//   onBack: () => void;
//   onMessageSelect: (evidences: Evidence[]) => void;
//   initialPrompt: string | null;
//   onInitialPromptPosted: () => void;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const mapApiTypeToUiType = (apiType: string = 'unknown'): Evidence['type'] => {
//     const type = apiType.toLowerCase();
//     switch (type) {
//         case 'log':
//         case 'metrics':
//         case 'text':
//             return type;
//         case 'yaml':
//             return 'code';
//         default:
//             return 'unknown';
//     }
// }

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceItems = msg.metadata?.evidence;
//     if (!evidenceItems || !Array.isArray(evidenceItems) || evidenceItems.length === 0) {
//         return [];
//     }
//     return evidenceItems.map((item, index) => {
//         let details = item.data;
//         let resource = item.k8s_object || 'Details';
//         try {
//             const parsed = JSON.parse(item.data);
//             details = JSON.stringify(parsed, null, 2);
//             if (resource === 'Details' && parsed.name) {
//                 resource = parsed.name;
//             }
//         } catch (e) { /* Not a JSON string, use raw data */ }
        
//         return {
//             id: `${msg.id}-evidence-${index}`,
//             type: mapApiTypeToUiType(item.type),
//             project: 'N/A',
//             resource: resource,
//             timestamp: msg.createdAt,
//             status: 'Normal',
//             details,
//         };
//     });
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, userProfilePicture,onBack, onMessageSelect, initialPrompt, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';
//   const [processMessages, setProcessMessages] = useState<ChatMessage[]>([]);
//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'Copilot',
//     hasEvidence: !!msg.metadata?.evidence?.length,
//     rawMessage: msg,
//   });

//   const { loading: isLoadingHistory, error: historyError, data } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     fetchPolicy: 'network-only'
//   });
//    useEffect(() => {
//     if (data?.getChatHistory) {
//       let processedHistory: ChatHistoryMessage[] = [...data.getChatHistory];

//       // Using your specific logic to filter the history
//       if (processedHistory.length > 2 && processedHistory[0].role === 'user' && processedHistory[2].role === 'agent') {
//          processedHistory = processedHistory.filter((_, index) => index !== 0 && index !== 2);
//       }
      
//       const historyMessages = processedHistory.map(mapApiMessageToChatMessage);
//       setMessages(historyMessages);
      
//      //Pass up evidence from all messages in the initial history load
//       // const allEvidences = processedHistory.flatMap(parseAndMapEvidence);
//       // onMessageSelect(allEvidences);
//     }
//   }, [data, onMessageSelect]);


//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onMessageSelect(newEvidences);
//         }

//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         setMessages(prev => prev.filter(m => m.id !== `temp-user-${Date.now()}`)); // Remove optimistic message on error
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: initialPrompt, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId]);

//   // const handleMessageClick = (message: ChatMessage) => {
//   //   if (!message.hasEvidence) {
//   //     setSelectedMessageId(null);
//   //     onMessageSelect([]);
//   //     return;
//   //   }
//   //   setSelectedMessageId(message.id);
//   //   const parsedEvidences = parseAndMapEvidence(message.rawMessage);
//   //   onMessageSelect(parsedEvidences);
//   // }
//   const handleMessageClick = (message: ChatMessage) => {
//     // Only allow agent messages to be interactive
//     if (message.sender !== 'agent') {
//       return;
//     }
    
//     // If clicking the already selected message, deselect it.
//     if(selectedMessageId === message.id) {
//       setSelectedMessageId(null);
//       onMessageSelect([]);
//       return;
//     }

//     setSelectedMessageId(message.id);
//     const parsedEvidences = parseAndMapEvidence(message.rawMessage);
//     onMessageSelect(parsedEvidences);
//   }


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: userMessageText, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   // const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//   //   const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//   //   return text
//   //     .split('\n')
//   //     .map((line, index) => {
//   //       const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//   //       const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//   //       return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//   //     });
//   // };
//   //  const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//   //   const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;

//   //   const markdownRules = [
//   //       { regex: /\*\*(.*?)\*\*/g, replacement: '<strong>$1</strong>' },
//   //       { regex: /\[(.*?)\]\((.*?)\)/g, replacement: `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>` },
//   //   ];

//   //   const paragraphs = text.split(/\n\s*\n/);

//   //   return paragraphs.map((paragraph, pIndex) => {
//   //       let processedLine = paragraph;
//   //       markdownRules.forEach(rule => {
//   //           processedLine = processedLine.replace(rule.regex, rule.replacement);
//   //       });
//   //       processedLine = processedLine.replace(/\n/g, '<br />');

//   //       return (
//   //           <Typography
//   //               key={pIndex}
//   //               component="p"
//   //               sx={{ 
//   //                   fontFamily:'Inter', 
//   //                   fontWeight: 500, 
//   //                   fontSize: '14px',
//   //                   mb: pIndex < paragraphs.length - 1 ? 1.5 : 0 ,
//   //                    lineHeight: 1.6 
//   //               }}
//   //               dangerouslySetInnerHTML={{ __html: processedLine }} 
//   //           />
//   //       );
//   //   });
//   // };


//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;

//     // Helper to apply inline markdown rules like bold, links, and inline code.
//     const applyInlineFormatting = (line: string): string => {
//         return line
//             .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
//             .replace(/`(.*?)`/g, '<code style="font-family: monospace; background-color: #e0e0e0; color: #333; padding: 2px 5px; border-radius: 4px; font-size: 0.9em;">$1</code>')
//             .replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" target="_blank" rel="noopener noreferrer" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//     };

//     const codeBlocks: string[] = [];
//     // 1. Extract fenced code blocks first and replace them with a placeholder
//     let processedText = text.replace(/```(\w*)\n([\s\S]*?)```/g, (match, lang, code) => {
//         codeBlocks.push(code);
//         return `\n\n__CODEBLOCK_${codeBlocks.length - 1}__\n\n`;
//     });

//     // 2. Split the remaining text into logical blocks by empty lines
//     const blocks = processedText.split(/\n\s*\n/).filter(block => block.trim() !== '');

//     return blocks.map((block, index) => {
//         // Check for our code block placeholder
//         const codeBlockMatch = block.match(/^__CODEBLOCK_(\d+)__$/);
//         if (codeBlockMatch) {
//             const codeIndex = parseInt(codeBlockMatch[1], 10);
//             return (
//                 <Box
//                     key={`code-${index}`}
//                     component="pre"
//                     sx={{
//                         fontFamily: 'monospace', fontSize: '13px', color: '#D4D4D4', bgcolor: '#1E1E1E',
//                         border: '1px solid #333', p: 1.5, my: 1, borderRadius: '4px',
//                         whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto'
//                     }}
//                 >
//                     {codeBlocks[codeIndex]}
//                 </Box>
//             );
//         }

//         // Check for Headings (###)
//         if (block.startsWith('### ')) {
//             const content = applyInlineFormatting(block.substring(4));
//             return <Typography key={index} variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontSize: '16px', fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: content }} />;
//         }

//         // Check for Unordered Lists (*)
//         if (block.startsWith('* ')) {
//             const listItems = block.split('\n').map(item => item.replace(/^\s*\*\s*/, ''));
//             return (
//                 <Box key={index} component="ul" sx={{ m: 0, p: 0, pl: 3, my: 1, '& li': { pl: 1, mb: 0.5 } }}>
//                     {listItems.map((li, liIndex) => (
//                         <Typography component="li" key={liIndex} sx={{ lineHeight: 1.6, fontSize: '14px' }}>
//                             <span dangerouslySetInnerHTML={{ __html: applyInlineFormatting(li) }} />
//                         </Typography>
//                     ))}
//                 </Box>
//             );
//         }

//         // Check for Ordered Lists (1.)
//         if (block.match(/^\d+\.\s/)) {
//             const listItems = block.split('\n').map(item => item.replace(/^\s*\d+\.\s*/, ''));
//             return (
//                 <Box key={index} component="ol" sx={{ m: 0, p: 0, pl: 3, my: 1, '& li': { pl: 1, mb: 0.5 } }}>
//                     {listItems.map((li, liIndex) => (
//                         <Typography component="li" key={liIndex} sx={{ lineHeight: 1.6, fontSize: '14px' }}>
//                             <span dangerouslySetInnerHTML={{ __html: applyInlineFormatting(li) }} />
//                         </Typography>
//                     ))}
//                 </Box>
//             );
//         }

//         // Default to a Paragraph, converting single newlines to <br>
//         const content = applyInlineFormatting(block).replace(/\n/g, '<br />');
//         return (
//             <Typography key={index} component="p" sx={{ fontFamily:'Inter', lineHeight: 1.6, fontSize: '14px',  fontWeight:500 }} dangerouslySetInnerHTML={{ __html: content }} />
//         );
//     });
//   };
//   return (
//       // <Paper elevation={0} sx={{ width: '790px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//       //   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//       //       <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//       //       <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//       //         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//       //           <IconButton size="small" onClick={onBack}><img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} /></IconButton>
//       //         </Box>
//       //          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//       //           <IconButton size="small"><img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} /></IconButton>
//       //         </Box>
//       //       </Box>
//       //   </Box>
//       //   <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//       //     {isLoadingHistory && <CircularProgress />}
//       //     {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//       //     {!isLoadingHistory && !historyError && messages.map((msg) => (
//       //       <Box key={msg.id} onClick={() => handleMessageClick(msg)} sx={{ cursor: msg.hasEvidence ? 'pointer' : 'default', mb: 3 }}>
//       //        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//       //           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//       //              <Avatar src={msg.sender === 'user' ? userAvatarUrl : finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//       //              <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//       //              <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//       //              <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//       //           </Box>
//       //           <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : (selectedMessageId === msg.id ? theme.palette.action.hover : '#FFFFFF'), color: '#333333', p: 1.5, borderRadius: '20px', border: selectedMessageId === msg.id ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent' }}>
//       //               {renderMessageText(msg.text, msg.sender)}
//       //           </Box>
//       //           {msg.sender === 'agent' && ( <Box sx={{ mt: 1 }}><IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton><IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton></Box>)}
//       //        </Box>
//       //       </Box>
//       //     ))}
//       //   </Box>
//       //   <Box sx={{ mt: 'auto', flexShrink: 1 }}>
//       //     <img src="/icons/half-gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '74.5%', left: '20.5%', transform: 'translate(-50%, -50%)', width: '485px', height: '400px', objectFit: 'contain', opacity: 1, zIndex: 0, borderRadius:'39px' }} />
//       //       <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '64px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
              
//       //         <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//       //           <TextField fullWidth multiline variant="standard" placeholder="Enter your prompt here..." value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())} disabled={isSending || isLoadingHistory} sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} />
//       //           <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//       //               <IconButton color="primary" onClick={handleSend} disabled={isSending || isLoadingHistory || !input.trim()} sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}>
//       //                 {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//       //               </IconButton>
//       //           </Box>
//       //         </Box>
//       //       </Paper>
//       //   </Box>
//       // </Paper>
//       <Paper elevation={0} sx={{ 
//         // minWidth: '450px', 
//         // maxWidth: '450px', 
//         width:'100%',
//         display: 'flex', 
//         flexDirection: 'column', 
//         p: 2.5, 
//         bgcolor: 'white', 
//         borderRadius: '12px', 
//         height: '100%',
//         position: 'relative', // Parent container for positioning
//         overflow: 'hidden'    // Clip the overflowing gradient
//       }}>
//         {/* All content is now a direct child of the main Paper */}
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0, zIndex: 1 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}><img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2, zIndex: 1 }}>
//           {/* {isLoadingHistory && <CircularProgress />} */}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} onClick={() => handleMessageClick(msg)} sx={{ cursor:msg.sender === 'agent' ? 'pointer' : 'default', mb: 3 }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? userProfilePicture : finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : (selectedMessageId === msg.id ? '#FFFFFF' : '#FFFFFF'), color: '#333333', p: 1.5, borderRadius: '20px', border: selectedMessageId === msg.id ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && ( <Box sx={{ mt: 1 }}><IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton><IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton></Box>)}
//              </Box>
//             </Box>
//           ))}
//         </Box>
        
//         {/* Gradient Image: Positioned absolutely within the main Paper */}
//         {/* <img 
//             src="/icons/half-gradient.svg" 
//             alt="background gradient" 
//             style={{ 
//                 position: 'absolute', 
//                 bottom: -50, // Position relative to the bottom
//                 left: -50,   // Position relative to the left
//                 width: '600px', 
//                 height: '400px', 
//                 objectFit: 'contain', 
//                 opacity: 1, 
//                 zIndex: 0, // Ensure it's behind the input box
//                 pointerEvents: 'none' // Make sure it's not clickable
//             }} 
//         /> */}
//         <img 
//             src="/icons/half-gradient.svg" 
//             alt="background gradient" 
//             style={{ 
//                 position: 'absolute', 
//                 bottom: '-10%', // Positioned with percentages
//                 left: '-15%',   // Positioned with percentages
//                 width: '130%',  // Scaled with percentages
//                 height: 'auto', // Auto height to maintain aspect ratio
//                 objectFit: 'contain', 
//                 opacity: 1, 
//                 zIndex: 0, 
//                 pointerEvents: 'none'
//             }} 
//         />
//         {/* Input Box Area */}
//         <Box sx={{ mt: 'auto', flexShrink: 0, zIndex: 1 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '64px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField fullWidth multiline variant="standard" placeholder="Enter your prompt here..." value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())} disabled={isSending || isLoadingHistory} sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton color="primary" onClick={handleSend} disabled={isSending || isLoadingHistory || !input.trim()} sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}>
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;





// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel';

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
//   hasEvidence: boolean;
//   rawMessage: ChatHistoryMessage;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: { type: string, k8s_object: string, data: string }[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onMessageSelect: (evidences: Evidence[]) => void;
//   initialPrompt: string | null;
//   onInitialPromptPosted: () => void;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const mapApiTypeToUiType = (apiType: string = 'unknown'): Evidence['type'] => {
//     const type = apiType.toLowerCase();
//     switch (type) {
//         case 'log':
//         case 'metrics':
//         case 'text':
//             return type;
//         case 'yaml':
//             return 'code';
//         default:
//             return 'unknown';
//     }
// }

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceItems = msg.metadata?.evidence;
//     if (!evidenceItems || !Array.isArray(evidenceItems) || evidenceItems.length === 0) {
//         return [];
//     }
//     return evidenceItems.map((item, index) => {
//         let details = item.data;
//         let resource = item.k8s_object || 'Details';
//         try {
//             const parsed = JSON.parse(item.data);
//             details = JSON.stringify(parsed, null, 2);
//             if (resource === 'Details' && parsed.name) {
//                 resource = parsed.name;
//             }
//         } catch (e) { /* Not a JSON string, use raw data */ }
        
//         return {
//             id: `${msg.id}-evidence-${index}`,
//             type: mapApiTypeToUiType(item.type),
//             project: 'N/A',
//             resource: resource,
//             timestamp: msg.createdAt,
//             status: 'Normal',
//             details,
//         };
//     });
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, onBack, onMessageSelect, initialPrompt, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';
//   const initialPromptPosted = useRef(false);

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'Copilot',
//     hasEvidence: !!msg.metadata?.evidence?.length,
//     rawMessage: msg,
//   });

//   const { loading: isLoadingHistory, error: historyError, data } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     fetchPolicy: 'network-only'
//   });

//    useEffect(() => {
//     if (data?.getChatHistory) {
//       let processedHistory: ChatHistoryMessage[] = [...data.getChatHistory];

//       if (processedHistory.length >= 4 && processedHistory[0].role === 'user' && processedHistory[2].role === 'agent') {
//          processedHistory = processedHistory.filter((_, index) => index !== 0 && index !== 2);
//       }
      
//       const historyMessages = processedHistory.map(mapApiMessageToChatMessage);
//       setMessages(historyMessages);
      
//       // Clear evidence panel on initial load, selection is now manual
//       onMessageSelect([]);
//     }
//   }, [data, onMessageSelect]);


//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onMessageSelect(newEvidences);
//             setSelectedMessageId(data.postMessage.id);
//         }

//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         setMessages(prev => prev.filter(m => !m.id.startsWith('temp-user-')));
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId && !initialPromptPosted.current) {
//       initialPromptPosted.current = true;
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: initialPrompt, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId, postMessage]);

//   useEffect(() => {
//     initialPromptPosted.current = false;
//   }, [chatId]);

//   const handleMessageClick = (message: ChatMessage) => {
//     // Only allow agent messages to be interactive
//     if (message.sender !== 'agent') {
//       return;
//     }
    
//     // If clicking the already selected message, deselect it.
//     if(selectedMessageId === message.id) {
//       setSelectedMessageId(null);
//       onMessageSelect([]);
//       return;
//     }

//     setSelectedMessageId(message.id);
//     const parsedEvidences = parseAndMapEvidence(message.rawMessage);
//     onMessageSelect(parsedEvidences);
//   }

//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: userMessageText, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;

//     // Helper to apply inline markdown rules like bold, links, and inline code.
//     const applyInlineFormatting = (line: string): string => {
//         return line
//             .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
//             .replace(/`(.*?)`/g, '<code style="font-family: monospace; background-color: #e0e0e0; color: #333; padding: 2px 5px; border-radius: 4px; font-size: 0.9em;">$1</code>')
//             .replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" target="_blank" rel="noopener noreferrer" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//     };

//     const codeBlocks: string[] = [];
//     // 1. Extract fenced code blocks first and replace them with a placeholder
//     let processedText = text.replace(/```(\w*)\n([\s\S]*?)```/g, (match, lang, code) => {
//         codeBlocks.push(code);
//         return `\n\n__CODEBLOCK_${codeBlocks.length - 1}__\n\n`;
//     });

//     // 2. Split the remaining text into logical blocks by empty lines
//     const blocks = processedText.split(/\n\s*\n/).filter(block => block.trim() !== '');

//     return blocks.map((block, index) => {
//         // Check for our code block placeholder
//         const codeBlockMatch = block.match(/^__CODEBLOCK_(\d+)__$/);
//         if (codeBlockMatch) {
//             const codeIndex = parseInt(codeBlockMatch[1], 10);
//             return (
//                 <Box
//                     key={`code-${index}`}
//                     component="pre"
//                     sx={{
//                         fontFamily: 'monospace', fontSize: '13px', color: '#D4D4D4', bgcolor: '#1E1E1E',
//                         border: '1px solid #333', p: 1.5, my: 1, borderRadius: '4px',
//                         whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto'
//                     }}
//                 >
//                     {codeBlocks[codeIndex]}
//                 </Box>
//             );
//         }

//         // Check for Headings (###)
//         if (block.startsWith('### ')) {
//             const content = applyInlineFormatting(block.substring(4));
//             return <Typography key={index} variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontSize: '16px', fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: content }} />;
//         }

//         // Check for Unordered Lists (*)
//         if (block.startsWith('* ')) {
//             const listItems = block.split('\n').map(item => item.replace(/^\s*\*\s*/, ''));
//             return (
//                 <Box key={index} component="ul" sx={{ m: 0, p: 0, pl: 3, my: 1, '& li': { pl: 1, mb: 0.5 } }}>
//                     {listItems.map((li, liIndex) => (
//                         <Typography component="li" key={liIndex} sx={{ lineHeight: 1.6, fontSize: '14px' }}>
//                             <span dangerouslySetInnerHTML={{ __html: applyInlineFormatting(li) }} />
//                         </Typography>
//                     ))}
//                 </Box>
//             );
//         }

//         // Check for Ordered Lists (1.)
//         if (block.match(/^\d+\.\s/)) {
//             const listItems = block.split('\n').map(item => item.replace(/^\s*\d+\.\s*/, ''));
//             return (
//                 <Box key={index} component="ol" sx={{ m: 0, p: 0, pl: 3, my: 1, '& li': { pl: 1, mb: 0.5 } }}>
//                     {listItems.map((li, liIndex) => (
//                         <Typography component="li" key={liIndex} sx={{ lineHeight: 1.6, fontSize: '14px' }}>
//                             <span dangerouslySetInnerHTML={{ __html: applyInlineFormatting(li) }} />
//                         </Typography>
//                     ))}
//                 </Box>
//             );
//         }

//         // Default to a Paragraph, converting single newlines to <br>
//         const content = applyInlineFormatting(block).replace(/\n/g, '<br />');
//         return (
//             <Typography key={index} component="p" sx={{ fontFamily:'Inter', lineHeight: 1.6, fontSize: '14px',  fontWeight:500 }} dangerouslySetInnerHTML={{ __html: content }} />
//         );
//     });
//    };

//   return (
//       <Paper elevation={0} sx={{ minWidth: '450px', maxWidth: '450px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}><img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && <CircularProgress />}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} onClick={() => handleMessageClick(msg)} sx={{ cursor: msg.sender === 'agent' ? 'pointer' : 'default', mb: 3 }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? userAvatarUrl : finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : (selectedMessageId === msg.id ? theme.palette.action.hover : '#FFFFFF'), color: '#333333', p: 1.5, borderRadius: '20px', border: selectedMessageId === msg.id ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && ( <Box sx={{ mt: 1 }}><IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton><IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton></Box>)}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 1 }}>
//           <img src="/icons/half-gradient.svg" alt="background gradient" style={{ position: 'absolute', bottom: -50, left: -50, width: '600px', height: '400px', objectFit: 'contain', opacity: 1, zIndex: 0, borderRadius:'39px', pointerEvents: 'none' }} />
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '64px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
              
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField fullWidth multiline variant="standard" placeholder="Enter your prompt here..." value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())} disabled={isSending || isLoadingHistory} sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton color="primary" onClick={handleSend} disabled={isSending || isLoadingHistory || !input.trim()} sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}>
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;








// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
//   hasEvidence: boolean;
//   rawMessage: ChatHistoryMessage;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: { type: string, k8s_object: string, data: string }[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onMessageSelect: (evidences: Evidence[]) => void;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceItems = msg.metadata?.evidence;
//     if (!evidenceItems || !Array.isArray(evidenceItems) || evidenceItems.length === 0) {
//         return [];
//     }
//     return evidenceItems.map((item, index) => {
//         let details = item.data;
//         try {
//             const parsed = JSON.parse(item.data);
//             details = JSON.stringify(parsed, null, 2);
//         } catch (e) { /* Not a JSON string, use raw data */ }
        
//         return {
//             id: `${msg.id}-evidence-${index}`,
//             type: (item.type || 'unknown').toLowerCase() as Evidence['type'],
//             project: 'N/A',
//             resource: item.k8s_object || 'Details',
//             timestamp: msg.createdAt,
//             status: 'Normal',
//             details,
//         };
//     });
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, onBack, onMessageSelect }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'CoPilot',
//     hasEvidence: !!msg.metadata?.evidence?.length,
//     rawMessage: msg,
//   });

//   const { loading: isLoadingHistory, error: historyError, data: historyData, stopPolling } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId,
//     pollInterval: 2500,
//     fetchPolicy: 'network-only'
//   });

//   useEffect(() => {
//     if (historyData?.getChatHistory) {
//       const history = historyData.getChatHistory;
//       const historyMessages = history.map(mapApiMessageToChatMessage);
//       setMessages(historyMessages);
      
//       const hasAgentResponse = history.some((msg: ChatHistoryMessage) => msg.role === 'agent');
//       if (hasAgentResponse) stopPolling();
//     }
//   }, [historyData, stopPolling]);

//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
//     }
//   });
  
//   const handleMessageClick = (message: ChatMessage) => {
//     if (!message.hasEvidence) {
//       setSelectedMessageId(null);
//       onMessageSelect([]); // Clear evidence panel if message has none
//       return;
//     }
//     setSelectedMessageId(message.id);
//     const parsedEvidences = parseAndMapEvidence(message.rawMessage);
//     onMessageSelect(parsedEvidences);
//   }

//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       stopPolling();
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user', text: userMessageText, timestamp: new Date().toISOString(),
//         name: userName || 'User', hasEvidence: false, rawMessage: {} as any,
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ minWidth: '450px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}><img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} /></IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && messages.length === 0 && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
          
//           {messages.map((msg) => (
//             <Box key={msg.id} onClick={() => handleMessageClick(msg)} sx={{ cursor: msg.hasEvidence ? 'pointer' : 'default', mb: 3 }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? userAvatarUrl : finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : (selectedMessageId === msg.id ? theme.palette.action.hover : '#FFFFFF'), color: '#333333', p: 1.5, borderRadius: '20px', border: selectedMessageId === msg.id ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && ( <Box sx={{ mt: 1 }}><IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton><IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton></Box>)}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField fullWidth multiline variant="standard" placeholder="Enter your prompt here..." value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())} disabled={isSending || (isLoadingHistory && messages.length === 0)} sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }} />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton color="primary" onClick={handleSend} disabled={isSending || (isLoadingHistory && messages.length === 0) || !input.trim()} sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}>
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;






// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: string[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onEvidenceUpdate: (evidence: Evidence[]) => void;
//   onInitialPromptPosted: () => void;
//   initialPrompt: string | null;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceStrings = msg.metadata?.evidence;

//     if (!evidenceStrings || !Array.isArray(evidenceStrings) || evidenceStrings.length === 0) {
//         return [];
//     }

//     const mappedEvidences: Evidence[] = [];
//     evidenceStrings.forEach((evidenceString: string, index: number) => {
//         try {
//             const parsedData = JSON.parse(evidenceString);
            
//             if (Array.isArray(parsedData)) {
//                 parsedData.forEach(item => {
//                     const details = `Resource Name: ${item.name}\nNamespace:     ${item.namespace}\nService Type:  ${item.type}\nCluster IP:    ${item.cluster_ip}`;
//                     mappedEvidences.push({
//                         id: `${msg.id}-${item.name}`,
//                         project: item.namespace || 'N/A',
//                         resource: item.name,
//                         timestamp: msg.createdAt,
//                         status: 'Normal',
//                         details: details,
//                     });
//                 });
//             } else {
//                  throw new Error("Parsed data is not an array, treating as single object/text.");
//             }
//         } catch (e) {
//             const nameMatch = evidenceString.match(/name:\s*(\S+)/);
//             const namespaceMatch = evidenceString.match(/namespace:\s*(\S+)/);
            
//             mappedEvidences.push({
//                 id: `raw-evidence-${msg.id}-${index}`,
//                 project: namespaceMatch ? namespaceMatch[1] : 'N/A',
//                 resource: nameMatch ? nameMatch[1] : 'Configuration Details',
//                 timestamp: msg.createdAt,
//                 status: 'Normal',
//                 details: evidenceString,
//             });
//         }
//     });
//     return mappedEvidences;
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, initialPrompt, onBack, onEvidenceUpdate, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'CoPilot',
//   });

//   const { loading: isLoadingHistory, error: historyError } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     onCompleted: (data) => {
//       if (data?.getChatHistory) {
//         let processedHistory: ChatHistoryMessage[] = data.getChatHistory;

//         // Per instruction, skip the first user message and the first agent response.
//         if (processedHistory.length >= 4 && processedHistory[0].role === 'user' && processedHistory[2].role === 'agent') {
//             //processedHistory = processedHistory.slice(2);
//            processedHistory = processedHistory.filter((_, index) => index !== 0 && index !== 2);
//         }
//         console.log("history", processedHistory);
//         const historyMessages = processedHistory.map(mapApiMessageToChatMessage);
//         setMessages(historyMessages);
        
//         // Evidence is extracted from the already-sliced history, so it's also skipped.
//         const allEvidences = processedHistory.flatMap(parseAndMapEvidence);
//         onEvidenceUpdate(allEvidences); // Call even if empty to clear previous evidence
//       }
//     },
//     fetchPolicy: 'network-only'
   
//   });
//   console.log("data", messages);
  
//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onEvidenceUpdate(newEvidences);
//         }

//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         const errorResponseMessage: ChatMessage = {
//             id: `temp-error-${Date.now()}`,
//             sender: 'agent',
//             text: 'Sorry, I encountered an error. Please try again.',
//             timestamp: new Date().toISOString(),
//             name: 'CoPilot',
//         };
//         setMessages(prev => [...prev, errorResponseMessage]);
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: initialPrompt,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId]);


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: userMessageText,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ width: '700px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}>
//                     <img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small">
//                     <img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && <CircularProgress />}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', color: '#333333', p: 1.5, borderRadius: '50px' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   variant="standard"
//                   placeholder="Enter your prompt here..."
//                   value={input}
//                   onChange={(e) => setInput(e.target.value)}
//                   onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                   disabled={isSending || isLoadingHistory}
//                   sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                   InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }}
//                 />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton
//                       color="primary"
//                       onClick={handleSend}
//                       disabled={isSending || isLoadingHistory || !input.trim()}
//                       sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                     >
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;



// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: string[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onEvidenceUpdate: (evidence: Evidence[]) => void;
//   onInitialPromptPosted: () => void;
//   initialPrompt: string | null;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceStrings = msg.metadata?.evidence;

//     if (!evidenceStrings || !Array.isArray(evidenceStrings) || evidenceStrings.length === 0) {
//         return [];
//     }

//     const mappedEvidences: Evidence[] = [];
//     evidenceStrings.forEach((evidenceString: string, index: number) => {
//         try {
//             const parsedData = JSON.parse(evidenceString);
            
//             if (Array.isArray(parsedData)) {
//                 parsedData.forEach(item => {
//                     const details = `Resource Name: ${item.name}\nNamespace:     ${item.namespace}\nService Type:  ${item.type}\nCluster IP:    ${item.cluster_ip}`;
//                     mappedEvidences.push({
//                         id: `${msg.id}-${item.name}`,
//                         project: item.namespace || 'N/A',
//                         resource: item.name,
//                         timestamp: msg.createdAt,
//                         status: 'Normal',
//                         details: details,
//                     });
//                 });
//             } else {
//                  throw new Error("Parsed data is not an array, treating as single object/text.");
//             }
//         } catch (e) {
//             const nameMatch = evidenceString.match(/name:\s*(\S+)/);
//             const namespaceMatch = evidenceString.match(/namespace:\s*(\S+)/);
            
//             mappedEvidences.push({
//                 id: `raw-evidence-${msg.id}-${index}`,
//                 project: namespaceMatch ? namespaceMatch[1] : 'N/A',
//                 resource: nameMatch ? nameMatch[1] : 'Configuration Details',
//                 timestamp: msg.createdAt,
//                 status: 'Normal',
//                 details: evidenceString,
//             });
//         }
//     });
//     return mappedEvidences;
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, initialPrompt, onBack, onEvidenceUpdate, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'CoPilot',
//   });

//   const { loading: isLoadingHistory, error: historyError } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     onCompleted: (data) => {
//       if (data?.getChatHistory) {
//         let history: ChatHistoryMessage[] = data.getChatHistory;

//         // Per instruction, don't display the first pair of user and agent responses.
//         if (history.length >= 2 && history[0].role === 'user' && history[1].role === 'agent') {
//             history = history.slice(2);
//         }

//         const historyMessages = history.map(mapApiMessageToChatMessage);
//         setMessages(historyMessages);
        
//         const allEvidences = history.flatMap(parseAndMapEvidence);
//         if (allEvidences.length > 0) {
//             onEvidenceUpdate(allEvidences);
//         }
//       }
//     },
//     fetchPolicy: 'network-only'
//   });

//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onEvidenceUpdate(newEvidences);
//         }

//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         const errorResponseMessage: ChatMessage = {
//             id: `temp-error-${Date.now()}`,
//             sender: 'agent',
//             text: 'Sorry, I encountered an error. Please try again.',
//             timestamp: new Date().toISOString(),
//             name: 'CoPilot',
//         };
//         setMessages(prev => [...prev, errorResponseMessage]);
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: initialPrompt,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId]);


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: userMessageText,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ minWidth: '450px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}>
//                     <img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small">
//                     <img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && <CircularProgress />}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', color: '#333333', p: 1.5, borderRadius: '50px' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   variant="standard"
//                   placeholder="Enter your prompt here..."
//                   value={input}
//                   onChange={(e) => setInput(e.target.value)}
//                   onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                   disabled={isSending || isLoadingHistory}
//                   sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                   InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }}
//                 />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton
//                       color="primary"
//                       onClick={handleSend}
//                       disabled={isSending || isLoadingHistory || !input.trim()}
//                       sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                     >
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;




// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: string[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onEvidenceUpdate: (evidence: Evidence[]) => void;
//   onInitialPromptPosted: () => void;
//   initialPrompt: string | null;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceStrings = msg.metadata?.evidence;

//     if (!evidenceStrings || !Array.isArray(evidenceStrings) || evidenceStrings.length === 0) {
//         return [];
//     }

//     const mappedEvidences: Evidence[] = [];
//     evidenceStrings.forEach((evidenceString: string, index: number) => {
//         try {
//             const parsedData = JSON.parse(evidenceString);
            
//             if (Array.isArray(parsedData)) {
//                 parsedData.forEach(item => {
//                     const details = `Resource Name: ${item.name}\nNamespace:     ${item.namespace}\nService Type:  ${item.type}\nCluster IP:    ${item.cluster_ip}`;
//                     mappedEvidences.push({
//                         id: `${msg.id}-${item.name}`,
//                         project: item.namespace || 'N/A',
//                         resource: item.name,
//                         timestamp: msg.createdAt,
//                         status: 'Normal',
//                         details: details,
//                     });
//                 });
//             } else {
//                  throw new Error("Parsed data is not an array, treating as single object/text.");
//             }
//         } catch (e) {
//             const nameMatch = evidenceString.match(/name:\s*(\S+)/);
//             const namespaceMatch = evidenceString.match(/namespace:\s*(\S+)/);
            
//             mappedEvidences.push({
//                 id: `raw-evidence-${msg.id}-${index}`,
//                 project: namespaceMatch ? namespaceMatch[1] : 'N/A',
//                 resource: nameMatch ? nameMatch[1] : 'Configuration Details',
//                 timestamp: msg.createdAt,
//                 status: 'Normal',
//                 details: evidenceString,
//             });
//         }
//     });
//     return mappedEvidences;
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, initialPrompt, onBack, onEvidenceUpdate, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'CoPilot',
//   });

//   const { loading: isLoadingHistory, error: historyError } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     onCompleted: (data) => {
//       if (data?.getChatHistory) {
//         let finalHistory: ChatHistoryMessage[] = [...data.getChatHistory];

//         // CORRECTED: De-duplication logic now ONLY targets the initial exchange.
//         if (
//             finalHistory.length >= 2 &&
//             finalHistory[0].role === 'user' &&
//             finalHistory[1].role === 'user' &&
//             finalHistory[0].content === finalHistory[1].content
//         ) {
//           // This handles the case where only the user message was duplicated.
//           finalHistory.shift(); 
//         }

//         const historyMessages = finalHistory.map(mapApiMessageToChatMessage);
//         setMessages(historyMessages);
        
//         const allEvidences = finalHistory.flatMap(parseAndMapEvidence);
//         if (allEvidences.length > 0) {
//             onEvidenceUpdate(allEvidences);
//         }
//       }
//     },
//     fetchPolicy: 'network-only'
//   });

//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onEvidenceUpdate(newEvidences);
//         }

//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         const errorResponseMessage: ChatMessage = {
//             id: `temp-error-${Date.now()}`,
//             sender: 'agent',
//             text: 'Sorry, I encountered an error. Please try again.',
//             timestamp: new Date().toISOString(),
//             name: 'CoPilot',
//         };
//         setMessages(prev => [...prev, errorResponseMessage]);
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: initialPrompt,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId]);


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: userMessageText,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ width: '700px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}>
//                     <img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small">
//                     <img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && <CircularProgress />}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', color: '#333333', p: 1.5, borderRadius: '50px' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   variant="standard"
//                   placeholder="Enter your prompt here..."
//                   value={input}
//                   onChange={(e) => setInput(e.target.value)}
//                   onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                   disabled={isSending || isLoadingHistory}
//                   sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                   InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }}
//                 />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton
//                       color="primary"
//                       onClick={handleSend}
//                       disabled={isSending || isLoadingHistory || !input.trim()}
//                       sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                     >
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;



// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: string[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onEvidenceUpdate: (evidence: Evidence[]) => void;
//   onInitialPromptPosted: () => void;
//   initialPrompt: string | null;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceStrings = msg.metadata?.evidence;

//     if (!evidenceStrings || !Array.isArray(evidenceStrings) || evidenceStrings.length === 0) {
//         return [];
//     }

//     const mappedEvidences: Evidence[] = [];
//     evidenceStrings.forEach((evidenceString: string, index: number) => {
//         try {
//             const parsedData = JSON.parse(evidenceString);
            
//             if (Array.isArray(parsedData)) {
//                 parsedData.forEach(item => {
//                     const details = `Resource Name: ${item.name}\nNamespace:     ${item.namespace}\nService Type:  ${item.type}\nCluster IP:    ${item.cluster_ip}`;
//                     mappedEvidences.push({
//                         id: `${msg.id}-${item.name}`,
//                         project: item.namespace || 'N/A',
//                         resource: item.name,
//                         timestamp: msg.createdAt,
//                         status: 'Normal',
//                         details: details,
//                     });
//                 });
//             } else {
//                  throw new Error("Parsed data is not an array, treating as single object/text.");
//             }
//         } catch (e) {
//             const nameMatch = evidenceString.match(/name:\s*(\S+)/);
//             const namespaceMatch = evidenceString.match(/namespace:\s*(\S+)/);
            
//             mappedEvidences.push({
//                 id: `raw-evidence-${msg.id}-${index}`,
//                 project: namespaceMatch ? namespaceMatch[1] : 'N/A',
//                 resource: nameMatch ? nameMatch[1] : 'Configuration Details',
//                 timestamp: msg.createdAt,
//                 status: 'Normal',
//                 details: evidenceString,
//             });
//         }
//     });
//     return mappedEvidences;
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, initialPrompt, onBack, onEvidenceUpdate, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'Copilot',
//   });

//   const { loading: isLoadingHistory, error: historyError } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     onCompleted: (data) => {
//       if (data?.getChatHistory) {
//         const history: ChatHistoryMessage[] = data.getChatHistory;

//         // Robust de-duplication to solve the "response comes twice" issue
//         const uniqueMessages: ChatHistoryMessage[] = [];
//         const seen = new Set<string>();
//         // Iterate backwards to keep the newest unique message
//         for (let i = history.length - 1; i >= 0; i--) {
//             const message = history[i];
//             const key = `${message.role}:${message.content}`;
//             if (!seen.has(key)) {
//                 uniqueMessages.push(message);
//                 seen.add(key);
//             }
//         }
//         const finalHistory = uniqueMessages.reverse(); // reverse back to oldest-first order

//         const historyMessages = finalHistory.map(mapApiMessageToChatMessage);
//         setMessages(historyMessages);
        
//         const allEvidences = finalHistory.flatMap(parseAndMapEvidence);
//         if (allEvidences.length > 0) {
//             onEvidenceUpdate(allEvidences);
//         }
//       }
//     },
//     fetchPolicy: 'network-only'
//   });

//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onEvidenceUpdate(newEvidences);
//         }

//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         const errorResponseMessage: ChatMessage = {
//             id: `temp-error-${Date.now()}`,
//             sender: 'agent',
//             text: 'Sorry, I encountered an error. Please try again.',
//             timestamp: new Date().toISOString(),
//             name: 'CoPilot',
//         };
//         setMessages(prev => [...prev, errorResponseMessage]);
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: initialPrompt,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId]);


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: userMessageText,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ width: '700px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}>
//                     <img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small">
//                     <img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && <CircularProgress />}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', color: '#333333', p: 1.5, borderRadius: '50px' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   variant="standard"
//                   placeholder="Enter your prompt here..."
//                   value={input}
//                   onChange={(e) => setInput(e.target.value)}
//                   onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                   disabled={isSending || isLoadingHistory}
//                   sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                   InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }}
//                 />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton
//                       color="primary"
//                       onClick={handleSend}
//                       disabled={isSending || isLoadingHistory || !input.trim()}
//                       sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                     >
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;





// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: string[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onEvidenceUpdate: (evidence: Evidence[]) => void;
//   onInitialPromptPosted: () => void;
//   initialPrompt: string | null;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceStrings = msg.metadata?.evidence;

//     if (!evidenceStrings || !Array.isArray(evidenceStrings) || evidenceStrings.length === 0) {
//         return [];
//     }

//     const mappedEvidences: Evidence[] = [];
//     evidenceStrings.forEach((evidenceString: string, index: number) => {
//         try {
//             const parsedData = JSON.parse(evidenceString);
            
//             if (Array.isArray(parsedData)) {
//                 parsedData.forEach(item => {
//                     const details = `Resource Name: ${item.name}\nNamespace:     ${item.namespace}\nService Type:  ${item.type}\nCluster IP:    ${item.cluster_ip}`;
//                     mappedEvidences.push({
//                         id: `${msg.id}-${item.name}`,
//                         project: item.namespace || 'N/A',
//                         resource: item.name,
//                         timestamp: msg.createdAt,
//                         status: 'Normal',
//                         details: details,
//                     });
//                 });
//             } else {
//                  throw new Error("Parsed data is not an array, treating as single object/text.");
//             }
//         } catch (e) {
//             const nameMatch = evidenceString.match(/name:\s*(\S+)/);
//             const namespaceMatch = evidenceString.match(/namespace:\s*(\S+)/);
            
//             mappedEvidences.push({
//                 id: `raw-evidence-${msg.id}-${index}`,
//                 project: namespaceMatch ? namespaceMatch[1] : 'N/A',
//                 resource: nameMatch ? nameMatch[1] : 'Configuration Details',
//                 timestamp: msg.createdAt,
//                 status: 'Normal',
//                 details: evidenceString,
//             });
//         }
//     });
//     return mappedEvidences;
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, initialPrompt, onBack, onEvidenceUpdate, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'CoPilot',
//   });

//   const { loading: isLoadingHistory, error: historyError } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     onCompleted: (data) => {
//       if (data?.getChatHistory) {
//         let history = [...data.getChatHistory];

//         // SOLVED: De-duplicate the initial user message if it appears twice.
//         if (
//           history.length >= 2 &&
//           history[0].role === 'user' &&
//           history[1].role === 'user' &&
//           history[0].content === history[1].content
//         ) {
//           history.shift(); // Remove the first instance of the duplicate
//         }

//         const historyMessages = history.map(mapApiMessageToChatMessage);
//         setMessages(historyMessages);
        
//         const allEvidences = history.flatMap(parseAndMapEvidence);
//         if (allEvidences.length > 0) {
//             onEvidenceUpdate(allEvidences);
//         }
//       }
//     },
//     fetchPolicy: 'network-only'
//   });

//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onEvidenceUpdate(newEvidences);
//         }

//         // SOLVED: Notify the parent that the initial prompt is handled only AFTER the mutation is complete.
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         const errorResponseMessage: ChatMessage = {
//             id: `temp-error-${Date.now()}`,
//             sender: 'agent',
//             text: 'Sorry, I encountered an error. Please try again.',
//             timestamp: new Date().toISOString(),
//             name: 'CoPilot',
//         };
//         setMessages(prev => [...prev, errorResponseMessage]);
//         if (initialPrompt) {
//             onInitialPromptPosted();
//         }
//     }
//   });

//   useEffect(() => {
//     // This effect now only runs ONCE for a new chat.
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: initialPrompt,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages([newUserMessage]); // Optimistically show user's message
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//     }
//   }, [initialPrompt, chatId]);


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: userMessageText,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ width: '700px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}>
//                     <img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small">
//                     <img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && !initialPrompt && (
//             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//                 <CircularProgress />
//             </Box>
//           )}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
          
//           {messages.map((msg) => (
//             <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', color: '#333333', p: 1.5, borderRadius: '50px' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   variant="standard"
//                   placeholder="Enter your prompt here..."
//                   value={input}
//                   onChange={(e) => setInput(e.target.value)}
//                   onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                   disabled={isSending || isLoadingHistory}
//                   sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                   InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }}
//                 />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton
//                       color="primary"
//                       onClick={handleSend}
//                       disabled={isSending || isLoadingHistory || !input.trim()}
//                       sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                     >
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;






// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined } from '@mui/icons-material'; // Add and History removed
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: string[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onEvidenceUpdate: (evidence: Evidence[]) => void;
//   onInitialPromptPosted: () => void;
//   initialPrompt: string | null;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceStrings = msg.metadata?.evidence;

//     if (!evidenceStrings || !Array.isArray(evidenceStrings) || evidenceStrings.length === 0) {
//         return [];
//     }

//     const mappedEvidences: Evidence[] = [];
//     evidenceStrings.forEach((evidenceString: string, index: number) => {
//         try {
//             const parsedData = JSON.parse(evidenceString);
            
//             if (Array.isArray(parsedData)) {
//                 parsedData.forEach(item => {
//                     const details = `Resource Name: ${item.name}\nNamespace:     ${item.namespace}\nService Type:  ${item.type}\nCluster IP:    ${item.cluster_ip}`;
//                     mappedEvidences.push({
//                         id: `${msg.id}-${item.name}`,
//                         project: item.namespace || 'N/A',
//                         resource: item.name,
//                         timestamp: msg.createdAt,
//                         status: 'Normal',
//                         details: details,
//                     });
//                 });
//             } else {
//                  throw new Error("Parsed data is not an array, treating as single object/text.");
//             }
//         } catch (e) {
//             const nameMatch = evidenceString.match(/name:\s*(\S+)/);
//             const namespaceMatch = evidenceString.match(/namespace:\s*(\S+)/);
            
//             mappedEvidences.push({
//                 id: `raw-evidence-${msg.id}-${index}`,
//                 project: namespaceMatch ? namespaceMatch[1] : 'N/A',
//                 resource: nameMatch ? nameMatch[1] : 'Configuration Details',
//                 timestamp: msg.createdAt,
//                 status: 'Normal',
//                 details: evidenceString,
//             });
//         }
//     });
//     return mappedEvidences;
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, initialPrompt, onBack, onEvidenceUpdate, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'CoPilot',
//   });

//   const { loading: isLoadingHistory, error: historyError } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     onCompleted: (data) => {
//       if (data?.getChatHistory) {
//         const historyMessages = data.getChatHistory.map(mapApiMessageToChatMessage);
//         setMessages(historyMessages);
        
//         const allEvidences = data.getChatHistory.flatMap(parseAndMapEvidence);
//         if (allEvidences.length > 0) {
//             onEvidenceUpdate(allEvidences);
//         }
//       }
//     },
//     fetchPolicy: 'network-only'
//   });

//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onEvidenceUpdate(newEvidences);
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         const errorResponseMessage: ChatMessage = {
//             id: `temp-error-${Date.now()}`,
//             sender: 'agent',
//             text: 'Sorry, I encountered an error. Please try again.',
//             timestamp: new Date().toISOString(),
//             name: 'CoPilot',
//         };
//         setMessages(prev => [...prev, errorResponseMessage]);
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: initialPrompt,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//       onInitialPromptPosted();
//     }
//   }, [initialPrompt, chatId, postMessage, onInitialPromptPosted]);


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: userMessageText,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ width: '700px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5, mx:'10px', flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small" onClick={onBack}>
//                     <img src="/icons/add-line.svg" alt="New Chat" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5, flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small">
//                     <img src="/icons/history-fill.svg" alt="History" style={{ width: 16, height: 16 }} />
//                 </IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && (
//             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//                 <CircularProgress />
//             </Box>
//           )}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', color: '#333333', p: 1.5, borderRadius: '50px' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   variant="standard"
//                   placeholder="Enter your prompt here..."
//                   value={input}
//                   onChange={(e) => setInput(e.target.value)}
//                   onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                   disabled={isSending || isLoadingHistory}
//                   sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                   InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }}
//                 />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton
//                       color="primary"
//                       onClick={handleSend}
//                       disabled={isSending || isLoadingHistory || !input.trim()}
//                       sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                     >
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;





// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined, Add, History } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: {
//       evidence: string[];
//       [key: string]: any;
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onEvidenceUpdate: (evidence: Evidence[]) => void;
//   onInitialPromptPosted: () => void;
//   initialPrompt: string | null;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// // CORRECTED: Enhanced parsing and mapping logic for richer evidence.
// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceStrings = msg.metadata?.evidence;

//     if (!evidenceStrings || !Array.isArray(evidenceStrings) || evidenceStrings.length === 0) {
//         return [];
//     }

//     const mappedEvidences: Evidence[] = [];
//     evidenceStrings.forEach((evidenceString: string, index: number) => {
//         try {
//             const parsedData = JSON.parse(evidenceString);
            
//             if (Array.isArray(parsedData)) {
//                 parsedData.forEach(item => {
//                     // Create a more detailed, multi-line description for JSON objects
//                     const details = `Resource Name: ${item.name}\nNamespace:     ${item.namespace}\nService Type:  ${item.type}\nCluster IP:    ${item.cluster_ip}`;
//                     mappedEvidences.push({
//                         id: `${msg.id}-${item.name}`,
//                         project: item.namespace || 'N/A', // Keep this mapping
//                         resource: item.name,
//                         timestamp: msg.createdAt,
//                         status: 'Normal',
//                         details: details,
//                     });
//                 });
//             } else {
//                  throw new Error("Parsed data is not an array, treating as single object/text.");
//             }
//         } catch (e) {
//             // For raw text (like YAML), try to find a name and namespace
//             const nameMatch = evidenceString.match(/name:\s*(\S+)/);
//             const namespaceMatch = evidenceString.match(/namespace:\s*(\S+)/);
            
//             mappedEvidences.push({
//                 id: `raw-evidence-${msg.id}-${index}`,
//                 project: namespaceMatch ? namespaceMatch[1] : 'N/A',
//                 resource: nameMatch ? nameMatch[1] : 'Configuration Details',
//                 timestamp: msg.createdAt,
//                 status: 'Normal',
//                 details: evidenceString,
//             });
//         }
//     });
//     return mappedEvidences;
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, initialPrompt, onEvidenceUpdate, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'CoPilot',
//   });

//   const { loading: isLoadingHistory, error: historyError } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     onCompleted: (data) => {
//       if (data?.getChatHistory) {
//         const historyMessages = data.getChatHistory.map(mapApiMessageToChatMessage);
//         setMessages(historyMessages);
        
//         const allEvidences = data.getChatHistory.flatMap(parseAndMapEvidence);
//         if (allEvidences.length > 0) {
//             onEvidenceUpdate(allEvidences);
//         }
//       }
//     },
//     fetchPolicy: 'network-only'
//   });

//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onEvidenceUpdate(newEvidences);
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         const errorResponseMessage: ChatMessage = {
//             id: `temp-error-${Date.now()}`,
//             sender: 'agent',
//             text: 'Sorry, I encountered an error. Please try again.',
//             timestamp: new Date().toISOString(),
//             name: 'CoPilot',
//         };
//         setMessages(prev => [...prev, errorResponseMessage]);
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: initialPrompt,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//       onInitialPromptPosted();
//     }
//   }, [initialPrompt, chatId, postMessage, onInitialPromptPosted]);


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: userMessageText,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ width: '640px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mx:'10px', flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><Add /></IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><History /></IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && (
//             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//                 <CircularProgress />
//             </Box>
//           )}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', color: '#333333', p: 1.5, borderRadius: '50px' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   variant="standard"
//                   placeholder="Enter your prompt here..."
//                   value={input}
//                   onChange={(e) => setInput(e.target.value)}
//                   onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                   disabled={isSending || isLoadingHistory}
//                   sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                   InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }}
//                 />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton
//                       color="primary"
//                       onClick={handleSend}
//                       disabled={isSending || isLoadingHistory || !input.trim()}
//                       sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                     >
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;




// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined, Add, History } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     // CORRECTED: metadata is an object, not a string to be parsed.
//     metadata: {
//       evidence: string[];
//       [key: string]: any; // Allows for other fields like 'response'
//     } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   onBack: () => void;
//   onEvidenceUpdate: (evidence: Evidence[]) => void;
//   onInitialPromptPosted: () => void;
//   initialPrompt: string | null;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// // CORRECTED: Rewritten to handle metadata as a pre-parsed object.
// const parseAndMapEvidence = (msg: ChatHistoryMessage): Evidence[] => {
//     const evidenceJsonStrings = msg.metadata?.evidence;

//     if (!evidenceJsonStrings || !Array.isArray(evidenceJsonStrings) || evidenceJsonStrings.length === 0) {
//         return [];
//     }

//     const mappedEvidences: Evidence[] = [];
//     evidenceJsonStrings.forEach((jsonString: string) => {
//         try {
//             // This inner parse is still needed as the 'evidence' array contains strings.
//             const parsedData: any[] = JSON.parse(jsonString);
//             parsedData.forEach(item => {
//                 mappedEvidences.push({
//                     id: item.name || `evidence-${Date.now()}`,
//                     project: item.namespace || 'N/A',
//                     resource: item.name,
//                     timestamp: msg.createdAt,
//                     status: 'Normal',
//                     details: `Type: ${item.type}, ClusterIP: ${item.cluster_ip}`,
//                 });
//             });
//         } catch (e) {
//             console.error("Failed to parse nested evidence JSON string:", e);
//         }
//     });
//     return mappedEvidences;
// };


// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, initialPrompt, onEvidenceUpdate, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'CoPilot',
//   });

//   const { loading: isLoadingHistory, error: historyError } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt,
//     onCompleted: (data) => {
//       if (data?.getChatHistory) {
//         const historyMessages = data.getChatHistory.map(mapApiMessageToChatMessage);
//         setMessages(historyMessages);
        
//         const allEvidences = data.getChatHistory.flatMap(parseAndMapEvidence);
//         if (allEvidences.length > 0) {
//             onEvidenceUpdate(allEvidences);
//         }
//       }
//     },
//     fetchPolicy: 'network-only'
//   });

//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
        
//         const newEvidences = parseAndMapEvidence(data.postMessage);
//         if (newEvidences.length > 0) {
//             onEvidenceUpdate(newEvidences);
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         const errorResponseMessage: ChatMessage = {
//             id: `temp-error-${Date.now()}`,
//             sender: 'agent',
//             text: 'Sorry, I encountered an error. Please try again.',
//             timestamp: new Date().toISOString(),
//             name: 'CoPilot',
//         };
//         setMessages(prev => [...prev, errorResponseMessage]);
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: initialPrompt,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//       onInitialPromptPosted();
//     }
//   }, [initialPrompt, chatId, postMessage, onInitialPromptPosted]);


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: userMessageText,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ minWidth: '450px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mx:'10px', flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><Add /></IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><History /></IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && (
//             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//                 <CircularProgress />
//             </Box>
//           )}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', color: '#333333', p: 1.5, borderRadius: '50px' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   variant="standard"
//                   placeholder="Enter your prompt here..."
//                   value={input}
//                   onChange={(e) => setInput(e.target.value)}
//                   onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                   disabled={isSending || isLoadingHistory}
//                   sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                   InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }}
//                 />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton
//                       color="primary"
//                       onClick={handleSend}
//                       disabled={isSending || isLoadingHistory || !input.trim()}
//                       sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                     >
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;




// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar,
//   Alert,
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined, Add, History } from '@mui/icons-material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import { Evidence } from './EvidencePanel'; // Adjust path as needed

// // GraphQL Definitions
// const GET_CHAT_HISTORY_QUERY = gql`
//   query GetChatHistory($sessionId: ID!) {
//     getChatHistory(sessionId: $sessionId) {
//       id
//       role
//       content
//       metadata 
//       createdAt
//     }
//   }
// `;

// const POST_MESSAGE_MUTATION = gql`
//   mutation PostMessage($input: PostMessageInput!) {
//     postMessage(input: $input) {
//       id
//       role
//       content
//       metadata 
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatHistoryMessage {
//     id: string;
//     role: 'user' | 'agent';
//     content: string;
//     metadata: { evidence: Evidence[] } | null;
//     createdAt: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   initialPrompt: string | null; // New prop for the first message
//   onBack: () => void;
//   onEvidenceUpdate: (evidence: Evidence[]) => void;
//   onInitialPromptPosted: () => void; // New prop to notify parent
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, initialPrompt, onEvidenceUpdate, onInitialPromptPosted }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';

//   const mapApiMessageToChatMessage = (msg: ChatHistoryMessage): ChatMessage => ({
//     id: msg.id,
//     sender: msg.role,
//     text: msg.content,
//     timestamp: msg.createdAt,
//     name: msg.role === 'user' ? (userName || 'User') : 'CoPilot',
//   });

//   const { loading: isLoadingHistory, error: historyError } = useQuery(GET_CHAT_HISTORY_QUERY, {
//     variables: { sessionId: chatId },
//     skip: !chatId || !!initialPrompt, // Skip if there's no ID OR if it's a new chat
//     onCompleted: (data) => {
//       if (data?.getChatHistory) {
//         const historyMessages = data.getChatHistory.map(mapApiMessageToChatMessage);
//         setMessages(historyMessages);
//         const evidenceFromHistory = data.getChatHistory
//           .filter((msg: ChatHistoryMessage) => msg.role === 'agent' && msg.metadata?.evidence)
//           .flatMap((msg: ChatHistoryMessage) => msg.metadata!.evidence);
//         onEvidenceUpdate(evidenceFromHistory);
//       }
//     },
//     fetchPolicy: 'network-only'
//   });

//   const [postMessage, { loading: isSending }] = useMutation(POST_MESSAGE_MUTATION, {
//     onCompleted: (data) => {
//         const agentMessage = mapApiMessageToChatMessage(data.postMessage);
//         setMessages(prev => [...prev, agentMessage]);
//         if (data.postMessage.metadata?.evidence) {
//           onEvidenceUpdate(data.postMessage.metadata.evidence);
//         }
//     },
//     onError: (error) => {
//         console.error("Failed to post message:", error);
//         const errorResponseMessage: ChatMessage = {
//             id: `temp-error-${Date.now()}`,
//             sender: 'agent',
//             text: 'Sorry, I encountered an error. Please try again.',
//             timestamp: new Date().toISOString(),
//             name: 'CoPilot',
//         };
//         setMessages(prev => [...prev, errorResponseMessage]);
//     }
//   });

//   useEffect(() => {
//     if (initialPrompt && chatId) {
//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: initialPrompt,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages([newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: initialPrompt } } });
//       onInitialPromptPosted();
//     }
//   }, [initialPrompt, chatId]);


//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = async () => {
//     if (input.trim() && !isSending) {
//       const userMessageText = input;
//       setInput('');

//       const newUserMessage: ChatMessage = {
//         id: `temp-user-${Date.now()}`,
//         sender: 'user',
//         text: userMessageText,
//         timestamp: new Date().toISOString(),
//         name: userName || 'User',
//       };
//       setMessages(prev => [...prev, newUserMessage]);
//       postMessage({ variables: { input: { sessionId: chatId, query: userMessageText } } });
//     }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper elevation={0} sx={{ minWidth: '450px', display: 'flex', flexDirection: 'column', p: 2.5, bgcolor: 'white', borderRadius: '12px', height: '100%' }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mx:'10px', flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><Add /></IconButton>
//               </Box>
//                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><History /></IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {isLoadingHistory && (
//             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//                 <CircularProgress />
//             </Box>
//           )}
//           {historyError && <Alert severity="error">Failed to load chat history: {historyError.message}</Alert>}
//           {!isLoadingHistory && !historyError && messages.map((msg) => (
//             <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 3, justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{msg.name}</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>|</Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>{formatDateDetail(msg.timestamp)}</Typography>
//                 </Box>
//                 <Box sx={{ bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF', color: '#333333', p: 1.5, borderRadius: '50px' }}>
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//             <Paper elevation={0} sx={{ p: '8px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', minHeight: '120px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
//               <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   variant="standard"
//                   placeholder="Enter your prompt here..."
//                   value={input}
//                   onChange={(e) => setInput(e.target.value)}
//                   onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                   disabled={isSending || isLoadingHistory}
//                   sx={{ flexGrow: 1, height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
//                   InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }}
//                 />
//                 <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                     <IconButton
//                       color="primary"
//                       onClick={handleSend}
//                       disabled={isSending || isLoadingHistory || !input.trim()}
//                       sx={{ bgcolor: '#0064FF', color: 'white', width: '32px', height: '32px', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }}
//                     >
//                       {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                     </IconButton>
//                 </Box>
//               </Box>
//             </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;


// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined, Add, History } from '@mui/icons-material';

// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   userEmail?: string | null;
//   onBack: () => void; // Added this line
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, userEmail, onBack }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const [isSending, setIsSending] = useState(false);
//   const chatContainerRef = useRef<HTMLDivElement>(null);
//   const userAvatarUrl = '/images/Ellipse 6912.svg';
//   const finOpsIconUrl = '/images/Chatbot.svg';
//   useEffect(() => {
//     const initialMessages: ChatMessage[] = [
//       { id: 'msg1', sender: 'user', text: "A pod in project 'cust-x' is stuck in pending", timestamp: '2024-01-12T20:41:00Z', name: userName || 'User', avatar: 'https://placehold.co/24x24/E2E8F0/4A5568?text=JD' },
//       {
//         id: 'msg2',
//         sender: 'agent',
//         text: "The pod [data-pod-abc](#) is failing to schedule due to CPU resource constraints on the nodes.\n\n\n<br>Resolution:\nCheck pod CPU requests. Click to inspect: [pod/data-pod-abc](#)\nCheck node capacity.",
//         timestamp: '2024-01-12T20:41:00Z',
//         name: 'CoPilot',
//         avatar: '/icons/agent-avatar.svg'
//       },
//       { id: 'msg3', sender: 'user', text: "Thank you for helping me", timestamp: '2024-01-12T20:41:00Z', name: userName || 'User', avatar: 'https://placehold.co/24x24/E2E8F0/4A5568?text=JD' },
//     ];
//     setMessages(initialMessages);
// }, [chatId, userName]); // This effect now depends on userName
// console.log('ChatDetailView received userName:', userName);
  
  
//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = () => {
//       if (input.trim()) {
//           setIsSending(true);
//           const newUserMessage: ChatMessage = {
//               id: `msg${messages.length + 1}`,
//               sender: 'user',
//               text: input,
//               timestamp: new Date().toISOString(),
//               name: userName || 'User',
//               avatar: '/images/Ellipse 6912.svg'
//           };
//           setTimeout(() => {
//               setMessages(prev => [...prev, newUserMessage]);
//               setInput('');
//               setIsSending(false);
//           }, 1000);
//       }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" sx={{fontFamily:'Inter', fontWeight: 500, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper
//         elevation={0}
//         sx={{
//           minWidth: '450px', // Will be controlled by parent
//           display: 'flex',
//           flexDirection: 'column',
//           p: 2.5,
//           bgcolor: 'white',
//           borderRadius: '12px',
//           height: '100%'
          
//         }}
//       >
      
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mx:'10px', flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><Add /></IconButton>
//               </Box>
              
//                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><History /></IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {messages.map((msg) => (
//             <Box
//               key={msg.id}
//               sx={{
//                 display: 'flex',
//                 flexDirection: 'row',
//                 alignItems: 'flex-start',
//                 mb: 3,
//                 justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
//               }}
//             >
             
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection:  'row' }}>
//                    <Avatar src={msg.sender === 'user' ? process.env.PUBLIC_URL + userAvatarUrl : process.env.PUBLIC_URL + finOpsIconUrl} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>
//                       {msg.name}
//                    </Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>
//                       |
//                    </Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>
//                       {formatDateDetail(msg.timestamp)}
//                    </Typography>
//                 </Box>
//                 <Box
//                   sx={{
                   
//                     bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF',
//                     color: msg.sender === 'user' ? '#333333' : '#333333',
//                     p: 1.5,
//                     borderRadius: '50px',
//                     //border: '1px solid #E5E7EB'
//                   }}
//                 >
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//           <Box>
//             <img
//                 src="/icons/Gradient.svg"
//                 alt="background gradient"
//                 style={{
//                   position: 'absolute',
//                   top: '82%',
//                   left: '20%',
//                   transform: 'translate(-50%, -50%)',
//                   width: '400px',
//                   height: '350px',
//                   objectFit: 'contain',
//                   zIndex: 0, // Placed on the bottom layer
//                   overflow:'hidden'
//                 }}
//             />
//           </Box>
//           <Paper 
//             elevation={0}
//             sx={{ 
//                 p: '8px',
//                 borderRadius: '16px',
//                 bgcolor: '#FFFFFF',
//                 border: '1px solid #E0E0E0',
//                 minHeight: '120px',
//                 display: 'flex',
//                 flexDirection: 'column',
//                 position: 'relative',      // Makes this the container for the absolute positioned image
//                 overflow: 'hidden',         // Clips the image to the Paper's rounded corners
//             }}
//           >
//             {/* Background Gradient Image: Positioned behind the content */}
            

//             {/* Content Wrapper: Positioned on top of the gradient */}
//             <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
//               <TextField
//                 fullWidth
//                 multiline
//                 variant="standard"
//                 placeholder="Enter your prompt here..."
//                 value={input}
//                 onChange={(e) => setInput(e.target.value)}
//                 onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//                 disabled={isSending}
//                 sx={{
//                     flexGrow: 1,
//                     height: '100%',
//                     '& .MuiInputBase-root': {
//                         height: '100%'
//                     },
//                 }}
//                 InputProps={{
//                   disableUnderline: true,
//                   style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' }
//                 }}
//               />
//               <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                   <IconButton
//                     color="primary"
//                     onClick={handleSend}
//                     disabled={isSending || !input.trim()}
//                     sx={{ 
//                         bgcolor: '#0064FF', 
//                         color: 'white', 
//                         width: '32px',
//                         height: '32px',
//                         '&:hover': { bgcolor: '#0052cc' },
//                         '&.Mui-disabled': { bgcolor: 'grey.300' }
//                       }}
//                   >
                    
//                     {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                   </IconButton>
//               </Box>
//             </Box>
//           </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;



// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   CircularProgress,
//   useTheme,
//   Avatar
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined, Add, History } from '@mui/icons-material';

// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// interface ChatDetailViewProps {
//   chatId: string;
//   userName?: string | null;
//   userEmail?: string | null;
//   onBack: () => void; // Added this line
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options);
// };

// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, userName, userEmail, onBack }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>([]);
//   const [input, setInput] = useState('');
//   const [isSending, setIsSending] = useState(false);
//   const chatContainerRef = useRef<HTMLDivElement>(null);
  
//   useEffect(() => {
//     const initialMessages: ChatMessage[] = [
//       { id: 'msg1', sender: 'user', text: "A pod in project 'cust-x' is stuck in pending", timestamp: '2024-01-12T20:41:00Z', name: userName || 'User', avatar: 'https://placehold.co/24x24/E2E8F0/4A5568?text=JD' },
//       {
//         id: 'msg2',
//         sender: 'agent',
//         text: "The pod [data-pod-abc](#) is failing to schedule due to CPU resource constraints on the nodes.\n\n**Resolution:**\nCheck pod CPU requests. Click to inspect: [pod/data-pod-abc](#)\nCheck node capacity.",
//         timestamp: '2024-01-12T20:41:00Z',
//         name: 'Cloud Agent',
//         avatar: '/icons/agent-avatar.svg'
//       },
//       { id: 'msg3', sender: 'user', text: "Thank you for helping me", timestamp: '2024-01-12T20:41:00Z', name: userName || 'User', avatar: 'https://placehold.co/24x24/E2E8F0/4A5568?text=JD' },
//     ];
//     setMessages(initialMessages);
// }, [chatId, userName]); // This effect now depends on userName
// console.log('ChatDetailView received userName:', userName);
  
  
//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = () => {
//       if (input.trim()) {
//           setIsSending(true);
//           const newUserMessage: ChatMessage = {
//               id: `msg${messages.length + 1}`,
//               sender: 'user',
//               text: input,
//               timestamp: new Date().toISOString(),
//               name: userName || 'User',
//               avatar: 'https://placehold.co/24x24/E2E8F0/4A5568?text=JD'
//           };
//           setTimeout(() => {
//               setMessages(prev => [...prev, newUserMessage]);
//               setInput('');
//               setIsSending(false);
//           }, 1000);
//       }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" variant="body1" sx={{lineHeight: 1.6, fontSize: '14px'}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper
//         elevation={0}
//         sx={{
//           minWidth: '450px', // Will be controlled by parent
//           display: 'flex',
//           flexDirection: 'column',
//           p: 2.5,
//           bgcolor: 'white',
//           borderRadius: '12px',
//           height: '100%'
//         }}
//       >
      
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{fontFamily:'Inter', fontWeight: 600, fontSize: '18px', color:'#333333' }}>Chat</Typography>
//             <Box sx={{display:'flex', alignItems:'center', gap:1}}>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mx:'10px', flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><Add /></IconButton>
//               </Box>
              
//                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row', width:'32px', height:'32px', bgcolor:'#F3F3F3',  borderRadius:'8px'}}>
//                 <IconButton size="small"><History /></IconButton>
//               </Box>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {messages.map((msg) => (
//             <Box
//               key={msg.id}
//               sx={{
//                 display: 'flex',
//                 flexDirection: 'row',
//                 alignItems: 'flex-start',
//                 mb: 3,
//                 justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
//               }}
//             >
             
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection:  'row' }}>
//                    <Avatar src={msg.sender === 'user' ? msg.avatar : undefined} sx={{ width: 16, height: 16, fontSize: '0.6rem' }} >{msg.name?.charAt(0)}</Avatar>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>
//                       {msg.name}
//                    </Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '10px', color:'#666666' }}>
//                       |
//                    </Typography>
//                    <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize: '12px', color:'#666666' }}>
//                       {formatDateDetail(msg.timestamp)}
//                    </Typography>
//                 </Box>
//                 <Box
//                   sx={{
//                     bgcolor: msg.sender === 'user' ? '#F2F7FF' : '#FFFFFF',
//                     color: msg.sender === 'user' ? '#333333' : '#333333',
//                     p: 1.5,
//                     borderRadius: '50px',
//                     //border: '1px solid #E5E7EB'
//                   }}
//                 >
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined sx={{ fontSize: '16px' }} /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
          
//           <Paper 
//             elevation={0}
//             sx={{ 
//                 p: '8px',
//                 borderRadius: '16px',
//             bgcolor: '#FFFFFF',
//             border: '1px solid #E0E0E0',
//                 minHeight: '120px',
//                 display: 'flex',
//                 flexDirection: 'column'
//             }}
//           >
            
//           <TextField
//             fullWidth
//             multiline
//             variant="standard"
//             placeholder="Enter your prompt here..."
//             value={input}
//             onChange={(e) => setInput(e.target.value)}
//             onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//             disabled={isSending}
//             sx={{
//                 flexGrow: 1,
//                 height: '100%',
//                  '& .MuiInputBase-root': {
//                     height: '100%'
//                 },
//             }}
//             InputProps={{
//               disableUnderline: true,
//               style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' }
//             }}
//           />
//             <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
//                 <IconButton
//                   color="primary"
//                   onClick={handleSend}
//                   disabled={isSending || !input.trim()}
//                   sx={{ 
//                       bgcolor: '#0064FF', 
//                       color: 'white', 
//                       width: '32px',
//                       height: '32px',
//                       '&:hover': { bgcolor: '#0052cc' },
//                       '&.Mui-disabled': { bgcolor: 'grey.300' }
//                     }}
//                 >
                  
//                   {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//                 </IconButton>
//             </Box>
//           </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;




// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   InputAdornment,
//   CircularProgress,
//   useTheme,
//   Avatar
// } from '@mui/material';
// import { ThumbUpOutlined, ThumbDownOutlined, Add, History } from '@mui/icons-material';

// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// const mockChatMessages: ChatMessage[] = [
//     { id: 'msg1', sender: 'user', text: "A pod in project 'cust-x' is stuck in pending", timestamp: '2024-01-12T20:41:00Z', name: 'John Doe', avatar: 'https://placehold.co/24x24/E2E8F0/4A5568?text=JD' },
//     {
//       id: 'msg2',
//       sender: 'agent',
//       text: "The pod [data-pod-abc](#) is failing to schedule due to CPU resource constraints on the nodes.\n\n**Resolution:**\nCheck pod CPU requests. Click to inspect: [pod/data-pod-abc](#)\nCheck node capacity.",
//       timestamp: '2024-01-12T20:41:00Z',
//       name: 'Cloud Agent',
//       avatar: '/icons/agent-avatar.svg'
//     },
//     { id: 'msg3', sender: 'user', text: "Thank you for helping me", timestamp: '2024-01-12T20:41:00Z', name: 'John Doe', avatar: 'https://placehold.co/24x24/E2E8F0/4A5568?text=JD' },
// ];

// interface ChatDetailViewProps {
//   chatId: string;
//   onBack: () => void;
// }

// const formatDateDetail = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, onBack }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>(mockChatMessages);
//   const [input, setInput] = useState('');
//   const [isSending, setIsSending] = useState(false);
//   const chatContainerRef = useRef<HTMLDivElement>(null);
  
//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = () => {
//       if (input.trim()) {
//           setIsSending(true);
//           const newUserMessage: ChatMessage = {
//               id: `msg${messages.length + 1}`,
//               sender: 'user',
//               text: input,
//               timestamp: new Date().toISOString(),
//               name: 'John Doe',
//               avatar: 'https://placehold.co/24x24/E2E8F0/4A5568?text=JD'
//           };
//           // Simulate API call
//           setTimeout(() => {
//               setMessages(prev => [...prev, newUserMessage]);
//               setInput('');
//               setIsSending(false);
//           }, 1000);
//       }
//   };
  
//   const renderMessageText = (text: string, sender: 'user' | 'agent') => {
//     const linkColor = sender === 'user' ? '#FFFFFF' : theme.palette.primary.main;
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${linkColor}; text-decoration: underline;">$1</a>`);
//         return <Typography key={index} component="p" variant="body1" sx={{lineHeight: 1.7}} dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//       <Paper
//         elevation={0}
//         sx={{
//           width: '450px',
//           display: 'flex',
//           flexDirection: 'column',
//           p: 2.5,
//           bgcolor: '#FFFFFF',
//           borderRadius: '12px',
//           height: '100%'
//         }}
//       >
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
//             <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1rem' }}>Chat</Typography>
//             <Box>
//                 <IconButton size="small"><Add /></IconButton>
//                 <IconButton size="small"><History /></IconButton>
//             </Box>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {messages.map((msg) => (
//             <Box
//               key={msg.id}
//               sx={{
//                 display: 'flex',
//                 flexDirection: 'row',
//                 alignItems: 'flex-start',
//                 mb: 3,
//                 justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
//               }}
//             >
//              {msg.sender === 'agent' && <Avatar src={msg.avatar} sx={{ width: 24, height: 24, mr: 1.5, mt: 0.5 }} />}
//              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: 'row' }}>
//                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
//                       {msg.name}
//                    </Typography>
//                    <Typography variant="caption" color="text.secondary">
//                       {formatDateDetail(msg.timestamp)}
//                    </Typography>
//                 </Box>
//                 <Box
//                   sx={{
//                     bgcolor: msg.sender === 'user' ? 'primary.main' : 'white',
//                     color: msg.sender === 'user' ? 'white' : 'text.primary',
//                     p: 1.5,
//                     borderRadius: '8px',
//                     border: msg.sender === 'agent' ? '1px solid #e0e0e0' : 'none',
//                   }}
//                 >
//                     {renderMessageText(msg.text, msg.sender)}
//                 </Box>
//                 {msg.sender === 'agent' && (
//                     <Box sx={{ mt: 1 }}>
//                         <IconButton size="small"><ThumbUpOutlined fontSize="small" /></IconButton>
//                         <IconButton size="small"><ThumbDownOutlined fontSize="small" /></IconButton>
//                     </Box>
//                 )}
//              </Box>
//              {msg.sender === 'user' && <Avatar src={msg.avatar} sx={{ width: 24, height: 24, ml: 1.5, mt: 0.5 }} />}
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//           <Paper 
//             elevation={0}
//             sx={{ 
//                 display: 'flex', 
//                 alignItems: 'center', 
//                 p: '4px',
//                 borderRadius: '12px',
//                 border: '1px solid #e0e0e0',
//                 bgcolor: 'white'
//             }}
//           >
//           <TextField
//             fullWidth
//             multiline
//             maxRows={4}
//             variant="standard"
//             placeholder="Enter your prompt here..."
//             value={input}
//             onChange={(e) => setInput(e.target.value)}
//             onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSending && (e.preventDefault(), handleSend())}
//             disabled={isSending}
//             sx={{
//                 flexGrow: 1,
//                 '& .MuiInputBase-root': {
//                     p: '8px'
//                 },
//             }}
//             InputProps={{
//               disableUnderline: true,
//             }}
//           />
//             <IconButton
//               color="primary"
//               onClick={handleSend}
//               disabled={isSending || !input.trim()}
//               sx={{ 
//                   bgcolor: '#0064FF', 
//                   color: 'white', 
//                   width: '32px',
//                   height: '32px',
//                   '&:hover': { bgcolor: '#0052cc' },
//                   '&.Mui-disabled': { bgcolor: 'grey.300' }
//                 }}
//             >
//               {isSending ? <CircularProgress size={16} color="inherit" /> : <img src="/icons/send.svg" alt="send" />}
//             </IconButton>
//           </Paper>
//         </Box>
//       </Paper>
//   );
// };

// export default ChatDetailView;






// import React, { useState, useRef, useEffect } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   InputAdornment,
//   CircularProgress,
//   useTheme
// } from '@mui/material';
// import { Send as SendIcon, ThumbUpOutlined, ThumbDownOutlined, ArrowBack } from '@mui/icons-material';
// import EvidencePanel, { Evidence } from './EvidencePanel';

// interface ChatMessage {
//   id: string;
//   sender: 'user' | 'agent';
//   text: string;
//   timestamp: string;
//   avatar?: string;
//   name?: string;
// }

// const mockChatMessages: ChatMessage[] = [
//     { id: 'msg1', sender: 'user', text: "A pod in project 'cust-x' is stuck in pending", timestamp: '2024-01-12T20:41:00Z', name: 'John Doe' },
//     {
//       id: 'msg2',
//       sender: 'agent',
//       text: "The pod [data-pod-abc](#) is failing to schedule due to CPU resource constraints on the nodes.\n\n**Resolution:**\nCheck pod CPU requests. Click to inspect: [pod/data-pod-abc](#)\nCheck node capacity.",
//       timestamp: '2024-01-12T20:41:00Z',
//       name: 'Cloud Agent'
//     },
//     { id: 'msg3', sender: 'user', text: "Thank you for helping me", timestamp: '2024-01-12T20:41:00Z', name: 'John Doe' },
// ];

// const mockEvidences: Evidence[] = [
//     {
//         id: 'ev1',
//         project: 'PROJECT: CUST-X',
//         resource: 'pod/data-pod-abc',
//         timestamp: '2024-01-12T20:41:00Z',
//         status: 'Failed to schedule',
//         details: '0/3 nodes available: 3 insufficient CPUs.'
//     },
//     {
//         id: 'ev2',
//         project: 'PROJECT: CUST-X',
//         resource: 'pod/data-pod-abc',
//         timestamp: '2024-01-12T20:41:00Z',
//         status: 'Failed to schedule',
//         details: '0/3 nodes available: 3 insufficient CPUs.'
//     },
// ];

// interface ChatDetailViewProps {
//   chatId: string;
//   onBack: () => void;
// }

// const formatDate = (dateString: string) => {
//     const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
//     return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const ChatDetailView: React.FC<ChatDetailViewProps> = ({ chatId, onBack }) => {
//   const theme = useTheme();
//   const [messages, setMessages] = useState<ChatMessage[]>(mockChatMessages);
//   const [input, setInput] = useState('');
//   const [isSending, setIsSending] = useState(false);
//   const chatContainerRef = useRef<HTMLDivElement>(null);
  
//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [messages]);

//   const handleSend = () => {
//       if (input.trim()) {
//           setIsSending(true);
//           const newUserMessage: ChatMessage = {
//               id: `msg${messages.length + 1}`,
//               sender: 'user',
//               text: input,
//               timestamp: new Date().toISOString(),
//               name: 'John Doe',
//           };
//           // Simulate API call
//           setTimeout(() => {
//               setMessages(prev => [...prev, newUserMessage]);
//               setInput('');
//               setIsSending(false);
//           }, 1000);
//       }
//   };
  
//   const renderMessageText = (text: string) => {
//     return text
//       .split('\n')
//       .map((line, index) => {
//         const boldedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
//         const linkedLine = boldedLine.replace(/\[(.*?)\]\((.*?)\)/g, `<a href="$2" style="color: ${theme.palette.primary.main}; text-decoration: none;">$1</a>`);
//         return <Typography key={index} component="p" variant="body1" dangerouslySetInnerHTML={{ __html: linkedLine }} />;
//       });
//   };

//   return (
//     <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
//       <Paper
//         elevation={0}
//         sx={{
//           width: { xs: '100%', lg: '50%' },
//           height: { xs: 'auto', lg: 'calc(100vh - 250px)' },
//           display: 'flex',
//           flexDirection: 'column',
//           p: 3,
//           bgcolor: 'white',
//           borderRadius: '12px',
//           border: '1px solid #e0e0e0',
//         }}
//       >
//         <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexShrink: 0 }}>
//             <IconButton onClick={onBack} sx={{ mr: 1 }}>
//                 <ArrowBack />
//             </IconButton>
//             <Typography variant="h6" sx={{ fontWeight: 600 }}>Chat</Typography>
//         </Box>
//         <Box ref={chatContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 1, mb: 2 }}>
//           {messages.map((msg) => (
//             <Box
//               key={msg.id}
//               sx={{
//                 display: 'flex',
//                 flexDirection: 'column',
//                 alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
//                 mb: 2,
//               }}
//             >
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row' }}>
//                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
//                     {msg.name}
//                  </Typography>
//                  <Typography variant="caption" color="text.secondary">
//                     {formatDate(msg.timestamp)}
//                  </Typography>
//               </Box>
//               <Box
//                 sx={{
//                   bgcolor: msg.sender === 'user' ? '#f0f0f0' : 'transparent',
//                   p: 1.5,
//                   borderRadius: '8px',
//                   maxWidth: '90%',
//                   border: msg.sender === 'agent' ? '1px solid #e0e0e0' : 'none',
//                 }}
//               >
//                   {renderMessageText(msg.text)}
//               </Box>
//               {msg.sender === 'agent' && (
//                   <Box sx={{ mt: 1 }}>
//                       <IconButton size="small"><ThumbUpOutlined fontSize="small" /></IconButton>
//                       <IconButton size="small"><ThumbDownOutlined fontSize="small" /></IconButton>
//                   </Box>
//               )}
//             </Box>
//           ))}
//         </Box>
//         <Box sx={{ mt: 'auto', flexShrink: 0 }}>
//           <TextField
//             fullWidth
//             variant="outlined"
//             placeholder="Enter your prompt here..."
//             value={input}
//             onChange={(e) => setInput(e.target.value)}
//             onKeyPress={(e) => e.key === 'Enter' && !isSending && handleSend()}
//             disabled={isSending}
//             sx={{
//                 '& .MuiOutlinedInput-root': {
//                     borderRadius: '12px',
//                     bgcolor: '#f5f5f5',
//                     '& fieldset': {
//                         border: 'none',
//                     },
//                 },
//             }}
//             InputProps={{
//               endAdornment: (
//                 <InputAdornment position="end">
//                   <IconButton
//                     color="primary"
//                     onClick={handleSend}
//                     disabled={isSending || !input.trim()}
//                     sx={{ bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' } }}
//                   >
//                     {isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
//                   </IconButton>
//                 </InputAdornment>
//               ),
//             }}
//           />
//         </Box>
//       </Paper>
//       <EvidencePanel evidences={mockEvidences} />
//     </Box>
//   );
// };

// export default ChatDetailView;
